import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Package, Layers, FileText, Plus, Edit, Trash2, Download, ImageIcon, CheckCircle, XCircle, BookOpen, LayoutGrid, Play, Video, BarChart3, TrendingUp, IndianRupee, Clock, Factory, Building2, Home, ChevronUp, ChevronDown, Palette, Type, Image, Grid3X3, ClipboardList, Settings2, Eye, Navigation, PanelBottom, Link2, Globe, GripVertical, LogIn } from "lucide-react";
import { getImageUrl } from "@/utils/imageUrl";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ImageUpload, VideoUpload, MultiImageUpload } from "@/components/FileUpload";
import axios from "axios";
import { toast } from "sonner";
import { generateQuotePDF } from "@/utils/pdfGenerator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Image Preview Component with validation
function ImagePreview({ url, className = "h-20 w-20" }) {
  const [status, setStatus] = useState("loading"); // loading, valid, invalid
  
  useEffect(() => {
    if (!url || url.trim() === "") {
      setStatus("empty");
      return;
    }
    
    setStatus("loading");
    const img = new Image();
    img.onload = () => setStatus("valid");
    img.onerror = () => setStatus("invalid");
    img.src = url;
  }, [url]);
  
  if (status === "empty") {
    return (
      <div className={`${className} border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center bg-gray-50`}>
        <ImageIcon className="h-6 w-6 text-gray-400" />
      </div>
    );
  }
  
  if (status === "loading") {
    return (
      <div className={`${className} border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center bg-gray-50 animate-pulse`}>
        <span className="text-xs text-gray-400">Loading...</span>
      </div>
    );
  }
  
  if (status === "invalid") {
    return (
      <div className={`${className} border-2 border-dashed border-red-300 rounded-md flex flex-col items-center justify-center bg-red-50`}>
        <XCircle className="h-5 w-5 text-red-500 mb-1" />
        <span className="text-xs text-red-500">Invalid URL</span>
      </div>
    );
  }
  
  return (
    <div className="relative">
      <img src={getImageUrl(url)} alt="Preview" className={`${className} object-cover rounded-md border border-green-300`} />
      <CheckCircle className="absolute -top-2 -right-2 h-5 w-5 text-green-500 bg-white rounded-full" />
    </div>
  );
}

// Multiple Image Preview Component
function MultiImagePreview({ urls }) {
  const imageList = urls.split(',').map(u => u.trim()).filter(Boolean);
  
  if (imageList.length === 0) {
    return (
      <div className="flex items-center gap-2 p-3 border border-dashed border-gray-300 rounded-md bg-gray-50">
        <ImageIcon className="h-5 w-5 text-gray-400" />
        <span className="text-sm text-gray-500">Enter image URLs above to see previews</span>
      </div>
    );
  }
  
  return (
    <div className="flex flex-wrap gap-3 p-3 border border-gray-200 rounded-md bg-gray-50">
      {imageList.map((url, index) => (
        <div key={index} className="flex flex-col items-center">
          <ImagePreview url={url} className="h-16 w-16" />
          <span className="text-xs text-gray-500 mt-1">Image {index + 1}</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("analytics");
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);
  const [showcaseCategories, setShowcaseCategories] = useState([]);
  const [showcaseProducts, setShowcaseProducts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [clientsRes, categoriesRes, productsRes, quotesRes, caseStudiesRes, showcaseCatRes, showcaseProdRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/admin/clients`),
        axios.get(`${API}/admin/categories`),
        axios.get(`${API}/admin/products`),
        axios.get(`${API}/admin/quotes`),
        axios.get(`${API}/admin/case-studies`),
        axios.get(`${API}/admin/showcase/categories`),
        axios.get(`${API}/admin/showcase/products`),
        axios.get(`${API}/admin/analytics`)
      ]);
      setClients(clientsRes.data);
      setCategories(categoriesRes.data);
      setProducts(productsRes.data);
      setQuotes(quotesRes.data);
      setCaseStudies(caseStudiesRes.data);
      setShowcaseCategories(showcaseCatRes.data);
      setShowcaseProducts(showcaseProdRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async (formData) => {
    try {
      await axios.post(`${API}/admin/clients`, formData);
      toast.success("Client created successfully");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create client");
    }
  };

  const handleCreateCategory = async (formData) => {
    try {
      await axios.post(`${API}/admin/categories`, formData);
      toast.success("Category created successfully");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to create category");
    }
  };

  const handleCreateProduct = async (formData) => {
    try {
      await axios.post(`${API}/admin/products`, formData);
      toast.success("Product created successfully");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to create product");
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    
    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success("Product deleted successfully");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;
    
    try {
      await axios.delete(`${API}/admin/clients/${clientId}`);
      toast.success("Client deleted successfully");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete client");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Are you sure you want to delete this category? All products in this category will need to be reassigned.")) return;
    
    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`);
      toast.success("Category deleted successfully");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete category");
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm("Are you sure you want to delete this quote?")) return;
    
    try {
      await axios.delete(`${API}/admin/quotes/${quoteId}`);
      toast.success("Quote deleted successfully");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete quote");
    }
  };

  const handleUpdateQuoteStatus = async (quoteId, status) => {
    try {
      await axios.put(`${API}/admin/quotes/${quoteId}/status?status=${status}`);
      toast.success("Quote status updated");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update quote status");
    }
  };

  const handleExportQuotePDF = (quote) => {
    try {
      const fileName = generateQuotePDF(quote);
      toast.success(`Quote exported as ${fileName}`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to export quote");
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-1">
        <section className="bg-gradient-to-br from-primary/10 to-secondary/10 border-b">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8">
            <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }} data-testid="admin-dashboard-heading">
              Admin Dashboard
            </h1>
            <p className="text-lg text-muted-foreground mt-2">Manage clients, products, categories, and quote requests</p>
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-5 sm:grid-cols-8 md:grid-cols-15 mb-8">
                <TabsTrigger value="analytics" className="gap-2" data-testid="tab-analytics">
                  <BarChart3 className="h-4 w-4" />
                  <span className="hidden sm:inline">Analytics</span>
                </TabsTrigger>
                <TabsTrigger value="clients" className="gap-2" data-testid="tab-clients">
                  <Users className="h-4 w-4" />
                  <span className="hidden sm:inline">Clients</span> ({clients.length})
                </TabsTrigger>
                <TabsTrigger value="categories" className="gap-2" data-testid="tab-categories">
                  <Layers className="h-4 w-4" />
                  <span className="hidden sm:inline">Categories</span> ({categories.length})
                </TabsTrigger>
                <TabsTrigger value="products" className="gap-2" data-testid="tab-products">
                  <Package className="h-4 w-4" />
                  <span className="hidden sm:inline">Products</span> ({products.length})
                </TabsTrigger>
                <TabsTrigger value="quotes" className="gap-2" data-testid="tab-quotes">
                  <FileText className="h-4 w-4" />
                  <span className="hidden sm:inline">Quotes</span> ({quotes.length})
                </TabsTrigger>
                <TabsTrigger value="case-studies" className="gap-2" data-testid="tab-case-studies">
                  <BookOpen className="h-4 w-4" />
                  <span className="hidden sm:inline">Case Studies</span> ({caseStudies.length})
                </TabsTrigger>
                <TabsTrigger value="showcase" className="gap-2" data-testid="tab-showcase">
                  <LayoutGrid className="h-4 w-4" />
                  <span className="hidden sm:inline">Showcase</span>
                </TabsTrigger>
                <TabsTrigger value="home-editor" className="gap-2" data-testid="tab-home-editor">
                  <Home className="h-4 w-4" />
                  <span className="hidden sm:inline">Home</span>
                </TabsTrigger>
                <TabsTrigger value="header-editor" className="gap-2" data-testid="tab-header-editor">
                  <Navigation className="h-4 w-4" />
                  <span className="hidden sm:inline">Header</span>
                </TabsTrigger>
                <TabsTrigger value="footer-editor" className="gap-2" data-testid="tab-footer-editor">
                  <PanelBottom className="h-4 w-4" />
                  <span className="hidden sm:inline">Footer</span>
                </TabsTrigger>
                <TabsTrigger value="login-editor" className="gap-2" data-testid="tab-login-editor">
                  <LogIn className="h-4 w-4" />
                  <span className="hidden sm:inline">Login Page</span>
                </TabsTrigger>
                <TabsTrigger value="manufacturing" className="gap-2" data-testid="tab-manufacturing">
                  <Factory className="h-4 w-4" />
                  <span className="hidden sm:inline">Manufacturing</span>
                </TabsTrigger>
                <TabsTrigger value="industries" className="gap-2" data-testid="tab-industries">
                  <Building2 className="h-4 w-4" />
                  <span className="hidden sm:inline">Industries</span>
                </TabsTrigger>
                <TabsTrigger value="access-form" className="gap-2" data-testid="tab-access-form">
                  <ClipboardList className="h-4 w-4" />
                  <span className="hidden sm:inline">Access Form</span>
                </TabsTrigger>
                <TabsTrigger value="favicon-editor" className="gap-2" data-testid="tab-favicon-editor">
                  <Globe className="h-4 w-4" />
                  <span className="hidden sm:inline">Favicon</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="analytics">
                <AnalyticsTab analytics={analytics} />
              </TabsContent>

              <TabsContent value="clients">
                <ClientsTab clients={clients} onCreate={handleCreateClient} onDelete={handleDeleteClient} />
              </TabsContent>

              <TabsContent value="categories">
                <CategoriesTab categories={categories} onCreate={handleCreateCategory} onDelete={handleDeleteCategory} />
              </TabsContent>

              <TabsContent value="products">
                <ProductsTab 
                  products={products} 
                  categories={categories} 
                  onCreate={handleCreateProduct}
                  onDelete={handleDeleteProduct}
                />
              </TabsContent>

              <TabsContent value="quotes">
                <QuotesTab quotes={quotes} onUpdateStatus={handleUpdateQuoteStatus} onExportPDF={handleExportQuotePDF} onDelete={handleDeleteQuote} />
              </TabsContent>

              <TabsContent value="case-studies">
                <CaseStudiesTab caseStudies={caseStudies} onRefresh={fetchAllData} />
              </TabsContent>

              <TabsContent value="showcase">
                <ShowcaseTab 
                  categories={showcaseCategories} 
                  products={showcaseProducts} 
                  onRefresh={fetchAllData} 
                />
              </TabsContent>

              <TabsContent value="home-editor">
                <HomeEditorTab />
              </TabsContent>

              <TabsContent value="header-editor">
                <HeaderEditorTab />
              </TabsContent>

              <TabsContent value="footer-editor">
                <FooterEditorTab />
              </TabsContent>

              <TabsContent value="login-editor">
                <LoginEditorTab />
              </TabsContent>

              <TabsContent value="manufacturing">
                <ManufacturingTab />
              </TabsContent>

              <TabsContent value="industries">
                <IndustriesTab />
              </TabsContent>

              <TabsContent value="access-form">
                <AccessFormTab />
              </TabsContent>

              <TabsContent value="favicon-editor">
                <FaviconEditorTab />
              </TabsContent>
            </Tabs>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

// Analytics Tab Component
function AnalyticsTab({ analytics }) {
  if (!analytics) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const { summary, quote_status, top_products, monthly_trends, recent_quotes } = analytics;

  const statusColors = {
    pending: "bg-yellow-100 text-yellow-800",
    reviewed: "bg-blue-100 text-blue-800",
    quoted: "bg-purple-100 text-purple-800",
    closed: "bg-green-100 text-green-800"
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Manrope' }}>Analytics Dashboard</h2>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <Card className="p-4 bg-gradient-to-br from-primary/10 to-primary/5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/20 rounded-lg">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>{summary.total_clients}</div>
              <div className="text-xs text-muted-foreground">Total Clients</div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 bg-gradient-to-br from-secondary/10 to-secondary/5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-secondary/20 rounded-lg">
              <Package className="h-5 w-5 text-secondary" />
            </div>
            <div>
              <div className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>{summary.total_products}</div>
              <div className="text-xs text-muted-foreground">Total Products</div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 bg-gradient-to-br from-blue-500/10 to-blue-500/5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Layers className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <div className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>{summary.total_categories}</div>
              <div className="text-xs text-muted-foreground">Categories</div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 bg-gradient-to-br from-green-500/10 to-green-500/5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <FileText className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <div className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>{summary.total_quotes}</div>
              <div className="text-xs text-muted-foreground">Total Quotes</div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 bg-gradient-to-br from-amber-500/10 to-amber-500/5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <IndianRupee className="h-5 w-5 text-amber-500" />
            </div>
            <div>
              <div className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>₹{(summary.total_quoted_value / 1000).toFixed(0)}K</div>
              <div className="text-xs text-muted-foreground">Quoted Value</div>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Quote Status Breakdown */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Manrope' }}>Quote Status Breakdown</h3>
          <div className="space-y-3">
            {Object.entries(quote_status).map(([status, count]) => {
              const total = Object.values(quote_status).reduce((a, b) => a + b, 0);
              const percentage = total > 0 ? ((count / total) * 100).toFixed(0) : 0;
              return (
                <div key={status} className="flex items-center gap-3">
                  <div className="w-24 text-sm capitalize">{status}</div>
                  <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
                    <div 
                      className={`h-full ${status === 'pending' ? 'bg-yellow-500' : status === 'reviewed' ? 'bg-blue-500' : status === 'quoted' ? 'bg-purple-500' : 'bg-green-500'}`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <div className="w-16 text-sm text-right font-medium">{count} ({percentage}%)</div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Top Requested Products */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Manrope' }}>Top Requested Products</h3>
          {top_products.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              No product requests yet
            </div>
          ) : (
            <div className="space-y-3">
              {top_products.map((product, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-sm">{product.name}</div>
                    <div className="text-xs text-muted-foreground">{product.total_quantity} units requested</div>
                  </div>
                  <div className="text-sm font-bold text-primary">{product.count} quotes</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trends */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Manrope' }}>Monthly Quote Trends</h3>
          {monthly_trends.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              No data available yet
            </div>
          ) : (
            <div className="flex items-end gap-2 h-40">
              {monthly_trends.map((item, idx) => {
                const maxCount = Math.max(...monthly_trends.map(t => t.count));
                const height = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                    <div 
                      className="w-full bg-primary/80 rounded-t-sm transition-all hover:bg-primary"
                      style={{ height: `${height}%`, minHeight: item.count > 0 ? '8px' : '0' }}
                      title={`${item.count} quotes`}
                    />
                    <div className="text-xs text-muted-foreground">{item.month.split('-')[1]}</div>
                    <div className="text-xs font-medium">{item.count}</div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Recent Quotes */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Manrope' }}>Recent Quotes</h3>
          {recent_quotes.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              No quotes yet
            </div>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {recent_quotes.slice(0, 5).map((quote, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                  <div>
                    <div className="text-sm font-medium">{quote.client_company}</div>
                    <div className="text-xs text-muted-foreground">{quote.products?.length || 0} products</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${statusColors[quote.status]}`}>
                      {quote.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// Clients Tab Component
function ClientsTab({ clients, onCreate, onDelete }) {
  const [showDialog, setShowDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    company_name: "",
    contact_person: "",
    phone: "",
    role: "client"
  });

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      email: client.email,
      password: "", // Leave empty, won't update unless filled
      company_name: client.company_name || "",
      contact_person: client.contact_person || "",
      phone: client.phone || "",
      role: client.role
    });
    setShowDialog(true);
  };

  const handleCreate = () => {
    setEditingClient(null);
    setFormData({
      email: "",
      password: "",
      company_name: "",
      contact_person: "",
      phone: "",
      role: "client"
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (editingClient) {
      // Update existing client
      try {
        const updateData = {
          email: formData.email,
          company_name: formData.company_name,
          contact_person: formData.contact_person,
          phone: formData.phone,
          role: formData.role
        };
        
        // Only include password if it's been filled in
        if (formData.password && formData.password.trim() !== "") {
          updateData.password = formData.password;
        }
        
        const response = await axios.put(`${API}/admin/clients/${editingClient.id}`, updateData);
        toast.success("Client updated successfully");
        window.location.reload(); // Refresh to show updated data
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to update client");
      }
    } else {
      // Create new client
      onCreate(formData);
    }
    
    setShowDialog(false);
    setEditingClient(null);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Client Accounts</h2>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="gap-2" onClick={handleCreate} data-testid="create-client-btn">
              <Plus className="h-4 w-4" />
              Create Client
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="create-client-dialog">
            <DialogHeader>
              <DialogTitle>{editingClient ? "Edit Client Account" : "Create New Client Account"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  data-testid="client-email-input"
                />
              </div>
              
              {editingClient && (
                <div className="p-4 bg-muted/30 rounded-sm border border-border">
                  <Label htmlFor="password" className="flex items-center gap-2">
                    <span>Change Password (Optional)</span>
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder="Leave blank to keep current password"
                    className="mt-2"
                    data-testid="client-password-input"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Enter a new password only if you want to change it. Leave blank to keep the existing password.
                  </p>
                </div>
              )}
              
              {!editingClient && (
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required
                    data-testid="client-password-input"
                  />
                </div>
              )}
              
              <div>
                <Label htmlFor="company_name">Company Name</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  data-testid="client-company-input"
                />
              </div>
              <div>
                <Label htmlFor="contact_person">Contact Person</Label>
                <Input
                  id="contact_person"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                  data-testid="client-contact-input"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  data-testid="client-phone-input"
                />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="submit-client-btn">
                  {editingClient ? "Update Client" : "Create Client"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {clients.map((client) => (
          <Card 
            key={client.id} 
            className="p-6 hover:border-primary/50 transition-colors" 
            data-testid={`client-card-${client.email}`}
          >
            <div className="flex justify-between items-start">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1 cursor-pointer" onClick={() => handleEdit(client)}>
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Email</div>
                  <div className="font-semibold">{client.email}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Company</div>
                  <div className="font-semibold">{client.company_name || "N/A"}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Contact Person</div>
                  <div className="font-semibold">{client.contact_person || "N/A"}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(client)}
                  data-testid={`edit-client-btn-${client.email}`}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(client.id)}
                  className="text-destructive hover:text-destructive"
                  data-testid={`delete-client-btn-${client.email}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Categories Tab Component
function CategoriesTab({ categories, onCreate, onDelete }) {
  const [showDialog, setShowDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    slug: "",
    image: "",
    display_order: 0
  });

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description,
      slug: category.slug,
      image: category.image,
      display_order: category.display_order
    });
    setShowDialog(true);
  };

  const handleCreate = () => {
    setEditingCategory(null);
    setFormData({ name: "", description: "", slug: "", image: "", display_order: 0 });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (editingCategory) {
      // Update existing category
      try {
        const response = await axios.put(`${API}/admin/categories/${editingCategory.id}`, formData);
        toast.success("Category updated successfully");
        window.location.reload();
      } catch (error) {
        toast.error("Failed to update category");
      }
    } else {
      // Create new category
      onCreate(formData);
    }
    
    setShowDialog(false);
    setEditingCategory(null);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Product Categories</h2>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="gap-2" onClick={handleCreate} data-testid="create-category-btn">
              <Plus className="h-4 w-4" />
              Create Category
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="create-category-dialog">
            <DialogHeader>
              <DialogTitle>{editingCategory ? "Edit Category" : "Create New Category"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value, slug: editingCategory ? formData.slug : e.target.value.toLowerCase().replace(/\s+/g, '-')})}
                  required
                  data-testid="category-name-input"
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                  data-testid="category-description-input"
                />
              </div>
              <ImageUpload
                value={formData.image}
                onChange={(url) => setFormData({...formData, image: url})}
                label="Category Image"
              />
              <div>
                <Label htmlFor="display_order">Display Order</Label>
                <Input
                  id="display_order"
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value)})}
                  data-testid="category-order-input"
                />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="submit-category-btn">
                  {editingCategory ? "Update Category" : "Create Category"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {categories.map((category) => (
          <Card 
            key={category.id} 
            className="overflow-hidden hover:border-primary/50 transition-colors" 
            data-testid={`category-card-${category.slug}`}
          >
            <div className="relative h-40 overflow-hidden cursor-pointer" onClick={() => handleEdit(category)}>
              <img src={getImageUrl(category.image)} alt={category.name} className="w-full h-full object-cover" />
              <div className="absolute top-2 right-2 flex gap-1">
                <Button
                  variant="secondary"
                  size="sm"
                  className="bg-white/90 hover:bg-white h-8 w-8 p-0"
                  onClick={(e) => { e.stopPropagation(); handleEdit(category); }}
                  data-testid={`edit-category-btn-${category.slug}`}
                >
                  <Edit className="h-4 w-4 text-primary" />
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  className="bg-white/90 hover:bg-red-50 h-8 w-8 p-0"
                  onClick={(e) => { e.stopPropagation(); onDelete(category.id); }}
                  data-testid={`delete-category-btn-${category.slug}`}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
            <div className="p-6 cursor-pointer" onClick={() => handleEdit(category)}>
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>{category.name}</h3>
              <p className="text-sm text-muted-foreground">{category.description}</p>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Products Tab Component
function ProductsTab({ products, categories, onCreate, onDelete }) {
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    category_id: "",
    name: "",
    code: "",
    images: "",
    material: "",
    dimensions: "",
    unit_weight: "",
    highlights: "",
    moq: 1,
    base_price: 0,
    packaging_cost: 0,
    sample_cost: "",
    price_tiers: "",
    video_url: ""
  });
  const [productImages, setProductImages] = useState([]);

  const handleEdit = (product) => {
    setEditingProduct(product);
    setProductImages(product.images || []);
    setFormData({
      category_id: product.category_id,
      name: product.name,
      code: product.code,
      material: product.material,
      dimensions: product.dimensions,
      unit_weight: product.unit_weight || "",
      highlights: product.highlights.join('\n'),
      moq: product.moq,
      base_price: product.base_price,
      packaging_cost: product.packaging_cost || 0,
      sample_cost: product.sample_cost || "",
      price_tiers: product.price_tiers ? JSON.stringify(product.price_tiers, null, 2) : "",
      video_url: product.video_url || ""
    });
    setShowDialog(true);
  };

  const handleCreate = () => {
    setEditingProduct(null);
    setProductImages([]);
    setFormData({
      category_id: "", name: "", code: "", material: "", dimensions: "", unit_weight: "",
      highlights: "", moq: 1, base_price: 0, packaging_cost: 0, sample_cost: "", price_tiers: "", video_url: ""
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const productData = {
      ...formData,
      images: productImages,
      highlights: formData.highlights.split('\n').filter(Boolean),
      moq: parseInt(formData.moq),
      base_price: parseFloat(formData.base_price),
      packaging_cost: parseFloat(formData.packaging_cost) || 0,
      sample_cost: formData.sample_cost ? parseFloat(formData.sample_cost) : null,
      price_tiers: formData.price_tiers ? JSON.parse(formData.price_tiers) : [],
      video_url: formData.video_url || null,
      unit_weight: formData.unit_weight || null,
      full_specs: {}
    };
    
    if (editingProduct) {
      // Update existing product
      try {
        const response = await axios.put(`${API}/admin/products/${editingProduct.id}`, productData);
        toast.success("Product updated successfully");
        window.location.reload();
      } catch (error) {
        toast.error("Failed to update product");
      }
    } else {
      // Create new product
      onCreate(productData);
    }
    
    setShowDialog(false);
    setEditingProduct(null);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Products</h2>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="gap-2" onClick={handleCreate} data-testid="create-product-btn">
              <Plus className="h-4 w-4" />
              Create Product
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="create-product-dialog">
            <DialogHeader>
              <DialogTitle>{editingProduct ? "Edit Product" : "Create New Product"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="category_id">Category</Label>
                <Select value={formData.category_id} onValueChange={(value) => setFormData({...formData, category_id: value})} required>
                  <SelectTrigger data-testid="product-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Product Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    data-testid="product-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="code">Product Code</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    required
                    data-testid="product-code-input"
                  />
                </div>
              </div>

              <MultiImageUpload
                value={productImages}
                onChange={setProductImages}
                label="Product Images"
                maxImages={5}
              />

              <VideoUpload
                value={formData.video_url}
                onChange={(url) => setFormData({...formData, video_url: url})}
                label="Product Video (optional - demo video)"
              />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="material">Material</Label>
                  <Input
                    id="material"
                    value={formData.material}
                    onChange={(e) => setFormData({...formData, material: e.target.value})}
                    required
                    data-testid="product-material-input"
                  />
                </div>
                <div>
                  <Label htmlFor="dimensions">Dimensions</Label>
                  <Input
                    id="dimensions"
                    value={formData.dimensions}
                    onChange={(e) => setFormData({...formData, dimensions: e.target.value})}
                    required
                    placeholder="e.g., 100x50x200 cm"
                    data-testid="product-dimensions-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="unit_weight">Unit Weight</Label>
                  <Input
                    id="unit_weight"
                    value={formData.unit_weight}
                    onChange={(e) => setFormData({...formData, unit_weight: e.target.value})}
                    placeholder="e.g., 15 kg"
                    data-testid="product-unit-weight-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="highlights">Highlights (one per line)</Label>
                <Textarea
                  id="highlights"
                  value={formData.highlights}
                  onChange={(e) => setFormData({...formData, highlights: e.target.value})}
                  rows={3}
                  placeholder="Heavy-duty construction\nModular design\nEasy installation"
                  data-testid="product-highlights-input"
                />
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="moq">MOQ</Label>
                  <Input
                    id="moq"
                    type="number"
                    value={formData.moq}
                    onChange={(e) => setFormData({...formData, moq: e.target.value})}
                    required
                    data-testid="product-moq-input"
                  />
                </div>
                <div>
                  <Label htmlFor="base_price">Base Price (₹)</Label>
                  <Input
                    id="base_price"
                    type="number"
                    step="0.01"
                    value={formData.base_price}
                    onChange={(e) => setFormData({...formData, base_price: e.target.value})}
                    required
                    data-testid="product-price-input"
                  />
                </div>
                <div>
                  <Label htmlFor="packaging_cost">Packaging Cost (₹)</Label>
                  <Input
                    id="packaging_cost"
                    type="number"
                    step="0.01"
                    value={formData.packaging_cost}
                    onChange={(e) => setFormData({...formData, packaging_cost: e.target.value})}
                    placeholder="0"
                    data-testid="product-packaging-input"
                  />
                </div>
                <div>
                  <Label htmlFor="sample_cost">Sample Cost (₹)</Label>
                  <Input
                    id="sample_cost"
                    type="number"
                    step="0.01"
                    value={formData.sample_cost}
                    onChange={(e) => setFormData({...formData, sample_cost: e.target.value})}
                    placeholder="Optional"
                    data-testid="product-sample-cost-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="price_tiers">Price Tiers (JSON format - optional)</Label>
                <Textarea
                  id="price_tiers"
                  value={formData.price_tiers}
                  onChange={(e) => setFormData({...formData, price_tiers: e.target.value})}
                  rows={4}
                  placeholder='[{"min_quantity": 50, "price_per_unit": 1500}, {"min_quantity": 100, "price_per_unit": 1350, "discount_percent": 5}, {"min_quantity": 250, "price_per_unit": 1200, "discount_percent": 10}]'
                  data-testid="product-tiers-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Add "discount_percent" to show discount badge (e.g., 5 for "5% off")</p>
              </div>

              <DialogFooter>
                <Button type="submit" data-testid="submit-product-btn">
                  {editingProduct ? "Update Product" : "Create Product"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {products.map((product) => (
          <Card 
            key={product.id} 
            className="p-6 cursor-pointer hover:border-primary/50 transition-colors" 
            onClick={() => handleEdit(product)}
            data-testid={`product-card-${product.code}`}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="mb-2">
                  <span className="text-xs font-bold tracking-widest uppercase text-muted-foreground">{product.code}</span>
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>{product.name}</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                  <div>
                    <div className="text-muted-foreground">Material</div>
                    <div className="font-medium">{product.material}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Dimensions</div>
                    <div className="font-medium">{product.dimensions}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">MOQ</div>
                    <div className="font-medium">{product.moq} units</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Unit Weight</div>
                    <div className="font-medium">{product.unit_weight || "N/A"}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Base Price</div>
                    <div className="font-medium">₹{product.base_price.toLocaleString()}</div>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Edit className="h-5 w-5 text-muted-foreground" />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(product.id);
                  }}
                  className="text-destructive hover:text-destructive"
                  data-testid={`delete-product-btn-${product.code}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Quotes Tab Component
function QuotesTab({ quotes, onUpdateStatus, onExportPDF, onDelete }) {
  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "reviewed", label: "Reviewed" },
    { value: "quoted", label: "Quoted" },
    { value: "closed", label: "Closed" }
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Manrope' }}>Quote Requests</h2>
      
      {quotes.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No Quote Requests</h3>
          <p className="text-muted-foreground">Client quote requests will appear here.</p>
        </Card>
      ) : (
        <div className="space-y-6">
          {quotes.map((quote) => (
            <Card key={quote.id} className="p-6" data-testid={`admin-quote-card-${quote.id}`}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="text-sm text-muted-foreground mb-1">
                    Quote ID: <span className="font-mono">{quote.id.substring(0, 8)}</span>
                  </div>
                  <div className="font-semibold text-lg">{quote.client_company}</div>
                  <div className="text-sm text-muted-foreground">{quote.client_email}</div>
                  <div className="text-sm text-muted-foreground">
                    Submitted: {new Date(quote.created_at).toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-40">
                    <Label className="text-xs">Status</Label>
                    <Select
                      value={quote.status}
                      onValueChange={(value) => onUpdateStatus(quote.id, value)}
                    >
                      <SelectTrigger className="mt-1" data-testid={`quote-status-select-${quote.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {statusOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="mt-5 flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onExportPDF(quote)}
                      className="gap-2"
                      data-testid={`admin-export-pdf-btn-${quote.id}`}
                    >
                      <Download className="h-4 w-4" />
                      Export
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDelete(quote.id)}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      data-testid={`delete-quote-btn-${quote.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="border-t border-border/50 pt-4">
                <h4 className="font-semibold mb-3">Products Requested</h4>
                <div className="space-y-2">
                  {quote.products.map((product, idx) => (
                    <div key={idx} className="flex justify-between items-center p-3 bg-muted/30 rounded-sm">
                      <div>
                        <span className="font-medium">{product.product_name}</span>
                        {product.notes && (
                          <span className="text-sm text-muted-foreground ml-2">({product.notes})</span>
                        )}
                      </div>
                      <span className="text-sm font-semibold">{product.quantity} units</span>
                    </div>
                  ))}
                </div>

                {quote.additional_notes && (
                  <div className="mt-4 p-4 bg-muted/30 rounded-sm">
                    <div className="text-sm font-semibold mb-1">Additional Notes:</div>
                    <p className="text-sm text-muted-foreground">{quote.additional_notes}</p>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// Case Studies Tab Component
function CaseStudiesTab({ caseStudies, onRefresh }) {
  const [showDialog, setShowDialog] = useState(false);
  const [editingCaseStudy, setEditingCaseStudy] = useState(null);
  const [formData, setFormData] = useState({
    client: "",
    industry: "",
    challenge: "",
    solution: "",
    results: "",
    image: "",
    display_order: 0
  });

  const handleEdit = (caseStudy) => {
    setEditingCaseStudy(caseStudy);
    setFormData({
      client: caseStudy.client,
      industry: caseStudy.industry,
      challenge: caseStudy.challenge,
      solution: caseStudy.solution,
      results: caseStudy.results.join('\n'),
      image: caseStudy.image,
      display_order: caseStudy.display_order || 0
    });
    setShowDialog(true);
  };

  const handleCreate = () => {
    setEditingCaseStudy(null);
    setFormData({
      client: "",
      industry: "",
      challenge: "",
      solution: "",
      results: "",
      image: "",
      display_order: caseStudies.length
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const caseStudyData = {
      ...formData,
      results: formData.results.split('\n').filter(Boolean),
      display_order: parseInt(formData.display_order) || 0
    };

    try {
      if (editingCaseStudy) {
        await axios.put(`${API}/admin/case-studies/${editingCaseStudy.id}`, caseStudyData);
        toast.success("Case study updated successfully");
      } else {
        await axios.post(`${API}/admin/case-studies`, caseStudyData);
        toast.success("Case study created successfully");
      }
      setShowDialog(false);
      setEditingCaseStudy(null);
      onRefresh();
    } catch (error) {
      toast.error(editingCaseStudy ? "Failed to update case study" : "Failed to create case study");
    }
  };

  const handleDelete = async (caseStudyId) => {
    if (!window.confirm("Are you sure you want to delete this case study?")) return;
    
    try {
      await axios.delete(`${API}/admin/case-studies/${caseStudyId}`);
      toast.success("Case study deleted successfully");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete case study");
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Case Studies</h2>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="gap-2" onClick={handleCreate} data-testid="create-case-study-btn">
              <Plus className="h-4 w-4" />
              Add Case Study
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="case-study-dialog">
            <DialogHeader>
              <DialogTitle>{editingCaseStudy ? "Edit Case Study" : "Create New Case Study"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="client">Client Name</Label>
                  <Input
                    id="client"
                    value={formData.client}
                    onChange={(e) => setFormData({...formData, client: e.target.value})}
                    required
                    placeholder="e.g., Major FMCG Brand"
                    data-testid="case-study-client-input"
                  />
                </div>
                <div>
                  <Label htmlFor="industry">Industry</Label>
                  <Input
                    id="industry"
                    value={formData.industry}
                    onChange={(e) => setFormData({...formData, industry: e.target.value})}
                    required
                    placeholder="e.g., FMCG & Snacks"
                    data-testid="case-study-industry-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="challenge">Challenge</Label>
                <Textarea
                  id="challenge"
                  value={formData.challenge}
                  onChange={(e) => setFormData({...formData, challenge: e.target.value})}
                  required
                  rows={3}
                  placeholder="Describe the client's challenge..."
                  data-testid="case-study-challenge-input"
                />
              </div>

              <div>
                <Label htmlFor="solution">Solution</Label>
                <Textarea
                  id="solution"
                  value={formData.solution}
                  onChange={(e) => setFormData({...formData, solution: e.target.value})}
                  required
                  rows={3}
                  placeholder="Describe the solution provided..."
                  data-testid="case-study-solution-input"
                />
              </div>

              <div>
                <Label htmlFor="results">Results (one per line)</Label>
                <Textarea
                  id="results"
                  value={formData.results}
                  onChange={(e) => setFormData({...formData, results: e.target.value})}
                  required
                  rows={4}
                  placeholder="500+ units delivered on time&#10;60% reduction in logistics costs&#10;40% increase in product visibility&#10;95% client satisfaction score"
                  data-testid="case-study-results-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Enter each result on a new line. These will appear as bullet points.</p>
              </div>

              <ImageUpload
                value={formData.image}
                onChange={(url) => setFormData({...formData, image: url})}
                label="Case Study Image"
              />

              <div>
                <Label htmlFor="display_order">Display Order</Label>
                <Input
                  id="display_order"
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({...formData, display_order: e.target.value})}
                  placeholder="0"
                  data-testid="case-study-order-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Lower numbers appear first</p>
              </div>

              <DialogFooter>
                <Button type="submit" data-testid="submit-case-study-btn">
                  {editingCaseStudy ? "Update Case Study" : "Create Case Study"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {caseStudies.length === 0 ? (
        <Card className="p-12 text-center">
          <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No Case Studies Yet</h3>
          <p className="text-muted-foreground mb-4">Add your first case study to showcase your success stories.</p>
          <Button onClick={handleCreate} className="gap-2">
            <Plus className="h-4 w-4" />
            Add First Case Study
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {caseStudies.map((cs) => (
            <Card 
              key={cs.id} 
              className="overflow-hidden cursor-pointer hover:border-primary/50 transition-colors" 
              onClick={() => handleEdit(cs)}
              data-testid={`case-study-card-${cs.id}`}
            >
              <div className="flex">
                <div className="w-48 h-32 flex-shrink-0">
                  <img 
                    src={getImageUrl(cs.image)} 
                    alt={cs.client} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/192x128?text=No+Image';
                    }}
                  />
                </div>
                <div className="flex-1 p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="inline-block px-2 py-0.5 bg-primary/10 text-primary text-xs font-semibold rounded mb-2">
                        {cs.industry}
                      </span>
                      <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope' }}>{cs.client}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{cs.challenge}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(cs);
                        }}
                        data-testid={`edit-case-study-btn-${cs.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(cs.id);
                        }}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-case-study-btn-${cs.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                    <span><strong>Results:</strong> {cs.results.length} items</span>
                    <span><strong>Order:</strong> {cs.display_order}</span>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// Showcase Control Centre Tab Component
function ShowcaseTab({ categories, products, onRefresh }) {
  const [activeSection, setActiveSection] = useState("categories");
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    slug: "",
    image: "",
    video: "",
    media_type: "image",
    display_order: 0
  });
  
  const [productForm, setProductForm] = useState({
    category_id: "",
    sku: "",
    heading: "",
    material: "",
    dimensions: "",
    unit_weight: "",
    use: "",
    description: "",
    image: "",
    video: "",
    display_order: 0
  });

  // Category handlers
  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      slug: category.slug,
      image: category.image || "",
      video: category.video || "",
      media_type: category.media_type || "image",
      display_order: category.display_order || 0
    });
    setShowCategoryDialog(true);
  };

  const handleCreateCategory = () => {
    setEditingCategory(null);
    setCategoryForm({
      name: "",
      slug: "",
      image: "",
      video: "",
      media_type: "image",
      display_order: categories.length
    });
    setShowCategoryDialog(true);
  };

  const handleSubmitCategory = async (e) => {
    e.preventDefault();
    const data = {
      ...categoryForm,
      slug: categoryForm.slug || categoryForm.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      display_order: parseInt(categoryForm.display_order) || 0
    };

    try {
      if (editingCategory) {
        await axios.put(`${API}/admin/showcase/categories/${editingCategory.id}`, data);
        toast.success("Category updated");
      } else {
        await axios.post(`${API}/admin/showcase/categories`, data);
        toast.success("Category created");
      }
      setShowCategoryDialog(false);
      setEditingCategory(null);
      onRefresh();
    } catch (error) {
      toast.error(editingCategory ? "Failed to update category" : "Failed to create category");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Delete this category and all its products?")) return;
    try {
      await axios.delete(`${API}/admin/showcase/categories/${categoryId}`);
      toast.success("Category deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete category");
    }
  };

  // Product handlers
  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      category_id: product.category_id,
      sku: product.sku,
      heading: product.heading,
      material: product.material,
      dimensions: product.dimensions,
      unit_weight: product.unit_weight || "",
      use: product.use,
      description: product.description || "",
      image: product.image || "",
      video: product.video || "",
      display_order: product.display_order || 0
    });
    setShowProductDialog(true);
  };

  const handleCreateProduct = () => {
    setEditingProduct(null);
    setProductForm({
      category_id: categories.length > 0 ? categories[0].id : "",
      sku: "",
      heading: "",
      material: "",
      dimensions: "",
      unit_weight: "",
      use: "",
      description: "",
      image: "",
      video: "",
      display_order: products.length
    });
    setShowProductDialog(true);
  };

  const handleSubmitProduct = async (e) => {
    e.preventDefault();
    const data = {
      ...productForm,
      display_order: parseInt(productForm.display_order) || 0
    };

    try {
      if (editingProduct) {
        await axios.put(`${API}/admin/showcase/products/${editingProduct.id}`, data);
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/admin/showcase/products`, data);
        toast.success("Product created");
      }
      setShowProductDialog(false);
      setEditingProduct(null);
      onRefresh();
    } catch (error) {
      toast.error(editingProduct ? "Failed to update product" : "Failed to create product");
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await axios.delete(`${API}/admin/showcase/products/${productId}`);
      toast.success("Product deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.name : "Unknown";
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Showcase Control Centre</h2>
          <p className="text-muted-foreground text-sm">Manage public product categories displayed on the home page</p>
        </div>
      </div>

      {/* Sub-navigation */}
      <div className="flex gap-4 mb-6 border-b">
        <button
          onClick={() => setActiveSection("categories")}
          className={`pb-3 px-1 font-medium transition-colors border-b-2 ${
            activeSection === "categories" 
              ? "border-primary text-primary" 
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          Categories ({categories.length})
        </button>
        <button
          onClick={() => setActiveSection("products")}
          className={`pb-3 px-1 font-medium transition-colors border-b-2 ${
            activeSection === "products" 
              ? "border-primary text-primary" 
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          Products ({products.length})
        </button>
      </div>

      {/* Categories Section */}
      {activeSection === "categories" && (
        <div>
          <div className="flex justify-end mb-4">
            <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
              <DialogTrigger asChild>
                <Button className="gap-2" onClick={handleCreateCategory} data-testid="create-showcase-category-btn">
                  <Plus className="h-4 w-4" />
                  Add Category
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingCategory ? "Edit Category" : "Create Category"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmitCategory} className="space-y-4">
                  <div>
                    <Label htmlFor="cat-name">Category Name</Label>
                    <Input
                      id="cat-name"
                      value={categoryForm.name}
                      onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                      required
                      placeholder="e.g., Floor Standing Units (FSU)"
                      data-testid="showcase-category-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cat-slug">Slug (URL-friendly)</Label>
                    <Input
                      id="cat-slug"
                      value={categoryForm.slug}
                      onChange={(e) => setCategoryForm({...categoryForm, slug: e.target.value})}
                      placeholder="e.g., fsu"
                      data-testid="showcase-category-slug-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Leave empty to auto-generate from name</p>
                  </div>
                  <ImageUpload
                    value={categoryForm.image}
                    onChange={(url) => setCategoryForm({...categoryForm, image: url})}
                    label="Category Image"
                  />
                  <VideoUpload
                    value={categoryForm.video}
                    onChange={(url) => setCategoryForm({...categoryForm, video: url})}
                    label="Category Video (YouTube, Vimeo, or upload)"
                  />
                  <div>
                    <Label>Display Media Type</Label>
                    <div className="flex gap-4 mt-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="media_type"
                          value="image"
                          checked={categoryForm.media_type === "image"}
                          onChange={(e) => setCategoryForm({...categoryForm, media_type: e.target.value})}
                          className="accent-primary"
                        />
                        <span className="text-sm">Show Image</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="media_type"
                          value="video"
                          checked={categoryForm.media_type === "video"}
                          onChange={(e) => setCategoryForm({...categoryForm, media_type: e.target.value})}
                          className="accent-primary"
                        />
                        <span className="text-sm">Show Video</span>
                      </label>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Choose which media to display on the home page</p>
                  </div>
                  <div>
                    <Label htmlFor="cat-order">Display Order</Label>
                    <Input
                      id="cat-order"
                      type="number"
                      value={categoryForm.display_order}
                      onChange={(e) => setCategoryForm({...categoryForm, display_order: e.target.value})}
                      data-testid="showcase-category-order-input"
                    />
                  </div>
                  <DialogFooter>
                    <Button type="submit" data-testid="submit-showcase-category-btn">
                      {editingCategory ? "Update" : "Create"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {categories.length === 0 ? (
            <Card className="p-12 text-center">
              <Layers className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Categories Yet</h3>
              <p className="text-muted-foreground mb-4">Add categories to display on your home page.</p>
              <Button onClick={handleCreateCategory} className="gap-2">
                <Plus className="h-4 w-4" />
                Add First Category
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((cat) => (
                <Card 
                  key={cat.id} 
                  className="overflow-hidden cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => handleEditCategory(cat)}
                >
                  <div className="h-32 bg-muted">
                    {cat.image ? (
                      <img src={getImageUrl(cat.image)} alt={cat.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-4xl font-bold text-muted-foreground/30">{cat.name.charAt(0)}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold">{cat.name}</h3>
                        <p className="text-xs text-muted-foreground">/{cat.slug}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleEditCategory(cat); }}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-destructive"
                          onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat.id); }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {products.filter(p => p.category_id === cat.id).length} products
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Products Section */}
      {activeSection === "products" && (
        <div>
          <div className="flex justify-end mb-4">
            <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
              <DialogTrigger asChild>
                <Button className="gap-2" onClick={handleCreateProduct} data-testid="create-showcase-product-btn">
                  <Plus className="h-4 w-4" />
                  Add Product
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingProduct ? "Edit Product" : "Create Product"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmitProduct} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="prod-sku">SKU</Label>
                      <Input
                        id="prod-sku"
                        value={productForm.sku}
                        onChange={(e) => setProductForm({...productForm, sku: e.target.value})}
                        required
                        placeholder="e.g., FSU-001"
                        data-testid="showcase-product-sku-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="prod-category">Category</Label>
                      <Select 
                        value={productForm.category_id} 
                        onValueChange={(v) => setProductForm({...productForm, category_id: v})}
                      >
                        <SelectTrigger data-testid="showcase-product-category-select">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="prod-heading">Heading</Label>
                    <Input
                      id="prod-heading"
                      value={productForm.heading}
                      onChange={(e) => setProductForm({...productForm, heading: e.target.value})}
                      required
                      placeholder="Product heading/title"
                      data-testid="showcase-product-heading-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="prod-material">Material</Label>
                      <Input
                        id="prod-material"
                        value={productForm.material}
                        onChange={(e) => setProductForm({...productForm, material: e.target.value})}
                        required
                        placeholder="e.g., Mild Steel, Aluminum"
                        data-testid="showcase-product-material-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="prod-dimensions">Dimensions</Label>
                      <Input
                        id="prod-dimensions"
                        value={productForm.dimensions}
                        onChange={(e) => setProductForm({...productForm, dimensions: e.target.value})}
                        required
                        placeholder="e.g., 1800 x 600 x 400 mm"
                        data-testid="showcase-product-dimensions-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="prod-unit-weight">Unit Weight</Label>
                      <Input
                        id="prod-unit-weight"
                        value={productForm.unit_weight}
                        onChange={(e) => setProductForm({...productForm, unit_weight: e.target.value})}
                        placeholder="e.g., 15 kg"
                        data-testid="showcase-product-unit-weight-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="prod-use">Use / Application</Label>
                    <Input
                      id="prod-use"
                      value={productForm.use}
                      onChange={(e) => setProductForm({...productForm, use: e.target.value})}
                      required
                      placeholder="e.g., MT / Hypermarket"
                      data-testid="showcase-product-use-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="prod-desc">Description</Label>
                    <Textarea
                      id="prod-desc"
                      value={productForm.description}
                      onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                      rows={3}
                      placeholder="Product description..."
                      data-testid="showcase-product-description-input"
                    />
                  </div>

                  <ImageUpload
                    value={productForm.image}
                    onChange={(url) => setProductForm({...productForm, image: url})}
                    label="Product Image"
                  />

                  <VideoUpload
                    value={productForm.video}
                    onChange={(url) => setProductForm({...productForm, video: url})}
                    label="Product Video (optional - demo video)"
                  />

                  <div>
                    <Label htmlFor="prod-order">Display Order</Label>
                    <Input
                      id="prod-order"
                      type="number"
                      value={productForm.display_order}
                      onChange={(e) => setProductForm({...productForm, display_order: e.target.value})}
                      data-testid="showcase-product-order-input"
                    />
                  </div>

                  <DialogFooter>
                    <Button type="submit" data-testid="submit-showcase-product-btn">
                      {editingProduct ? "Update" : "Create"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {products.length === 0 ? (
            <Card className="p-12 text-center">
              <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Products Yet</h3>
              <p className="text-muted-foreground mb-4">Add products to your showcase categories.</p>
              <Button onClick={handleCreateProduct} className="gap-2" disabled={categories.length === 0}>
                <Plus className="h-4 w-4" />
                Add First Product
              </Button>
              {categories.length === 0 && (
                <p className="text-xs text-muted-foreground mt-2">Create a category first</p>
              )}
            </Card>
          ) : (
            <div className="space-y-4">
              {products.map((prod) => (
                <Card 
                  key={prod.id} 
                  className="overflow-hidden cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => handleEditProduct(prod)}
                >
                  <div className="flex">
                    <div className="w-40 h-32 flex-shrink-0 bg-muted">
                      {prod.image ? (
                        <img src={getImageUrl(prod.image)} alt={prod.heading} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="h-10 w-10 text-muted-foreground/30" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="inline-block px-2 py-0.5 bg-primary/10 text-primary text-xs font-mono rounded mb-1">
                            {prod.sku}
                          </span>
                          <h3 className="font-semibold" style={{ fontFamily: 'Manrope' }}>{prod.heading}</h3>
                          <p className="text-xs text-muted-foreground mt-1">
                            {getCategoryName(prod.category_id)}
                          </p>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleEditProduct(prod); }}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-destructive"
                            onClick={(e) => { e.stopPropagation(); handleDeleteProduct(prod.id); }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                        <div>
                          <span className="text-muted-foreground">Material:</span>
                          <span className="ml-2">{prod.material}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Dimensions:</span>
                          <span className="ml-2">{prod.dimensions}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Use:</span>
                          <span className="ml-2">{prod.use}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============= MANUFACTURING TAB =============
function ManufacturingTab() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/page/manufacturing`);
      setContent(response.data);
    } catch (error) {
      toast.error("Failed to load manufacturing page content");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/page/manufacturing`, content, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Manufacturing page updated successfully");
    } catch (error) {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const updateCapability = (index, field, value) => {
    const newCapabilities = [...content.capabilities];
    newCapabilities[index] = { ...newCapabilities[index], [field]: value };
    setContent({ ...content, capabilities: newCapabilities });
  };

  const addCapability = () => {
    setContent({
      ...content,
      capabilities: [...content.capabilities, { icon: "Settings", title: "", description: "" }]
    });
  };

  const removeCapability = (index) => {
    const newCapabilities = content.capabilities.filter((_, i) => i !== index);
    setContent({ ...content, capabilities: newCapabilities });
  };

  const updateProcess = (index, field, value) => {
    const newSteps = [...content.process_steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setContent({ ...content, process_steps: newSteps });
  };

  const addProcess = () => {
    const nextStep = String(content.process_steps.length + 1).padStart(2, '0');
    setContent({
      ...content,
      process_steps: [...content.process_steps, { step: nextStep, title: "", description: "" }]
    });
  };

  const removeProcess = (index) => {
    const newSteps = content.process_steps.filter((_, i) => i !== index);
    setContent({ ...content, process_steps: newSteps });
  };

  const updateMaterial = (index, field, value) => {
    const newMaterials = [...content.materials];
    newMaterials[index] = { ...newMaterials[index], [field]: value };
    setContent({ ...content, materials: newMaterials });
  };

  const addMaterial = () => {
    setContent({
      ...content,
      materials: [...content.materials, { name: "", use: "" }]
    });
  };

  const removeMaterial = (index) => {
    const newMaterials = content.materials.filter((_, i) => i !== index);
    setContent({ ...content, materials: newMaterials });
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;
  if (!content) return <div className="text-center py-12">Failed to load content</div>;

  const iconOptions = ["Factory", "Cpu", "Recycle", "Settings", "CheckCircle", "TrendingUp", "Award", "Shield", "Zap", "Target"];

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Manufacturing Page</h2>
        <Button onClick={handleSave} disabled={saving} data-testid="save-manufacturing-btn">
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      {/* Hero Section */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Hero Section</h3>
        <div className="space-y-4">
          <div>
            <Label>Hero Title</Label>
            <Input 
              value={content.hero_title} 
              onChange={(e) => setContent({ ...content, hero_title: e.target.value })}
              data-testid="manufacturing-hero-title"
            />
          </div>
          <div>
            <Label>Hero Subtitle</Label>
            <Textarea 
              value={content.hero_subtitle} 
              onChange={(e) => setContent({ ...content, hero_subtitle: e.target.value })}
              rows={2}
              data-testid="manufacturing-hero-subtitle"
            />
          </div>
          <div>
            <Label>Hero Background Image URL</Label>
            <Input 
              value={content.hero_image} 
              onChange={(e) => setContent({ ...content, hero_image: e.target.value })}
              placeholder="https://images.unsplash.com/..."
            />
          </div>
        </div>
      </Card>

      {/* Capabilities Section */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Capabilities ({content.capabilities.length})</h3>
          <Button variant="outline" size="sm" onClick={addCapability}>
            <Plus className="h-4 w-4 mr-2" /> Add Capability
          </Button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Section Title</Label>
              <Input 
                value={content.capabilities_title} 
                onChange={(e) => setContent({ ...content, capabilities_title: e.target.value })}
              />
            </div>
            <div>
              <Label>Section Subtitle</Label>
              <Input 
                value={content.capabilities_subtitle} 
                onChange={(e) => setContent({ ...content, capabilities_subtitle: e.target.value })}
              />
            </div>
          </div>
          {content.capabilities.map((cap, index) => (
            <div key={index} className="p-4 border rounded-md space-y-3">
              <div className="flex justify-between items-start">
                <span className="text-sm font-medium text-muted-foreground">Capability {index + 1}</span>
                <Button variant="ghost" size="sm" className="text-destructive" onClick={() => removeCapability(index)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Icon</Label>
                  <Select value={cap.icon} onValueChange={(v) => updateCapability(index, "icon", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {iconOptions.map(icon => (
                        <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Title</Label>
                  <Input value={cap.title} onChange={(e) => updateCapability(index, "title", e.target.value)} />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={cap.description} onChange={(e) => updateCapability(index, "description", e.target.value)} rows={2} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Process Section */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Process Steps ({content.process_steps.length})</h3>
          <Button variant="outline" size="sm" onClick={addProcess}>
            <Plus className="h-4 w-4 mr-2" /> Add Step
          </Button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Section Title</Label>
              <Input 
                value={content.process_title} 
                onChange={(e) => setContent({ ...content, process_title: e.target.value })}
              />
            </div>
            <div>
              <Label>Section Subtitle</Label>
              <Input 
                value={content.process_subtitle} 
                onChange={(e) => setContent({ ...content, process_subtitle: e.target.value })}
              />
            </div>
          </div>
          {content.process_steps.map((step, index) => (
            <div key={index} className="p-4 border rounded-md space-y-3">
              <div className="flex justify-between items-start">
                <span className="text-sm font-medium text-muted-foreground">Step {step.step}</span>
                <Button variant="ghost" size="sm" className="text-destructive" onClick={() => removeProcess(index)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label>Step #</Label>
                  <Input value={step.step} onChange={(e) => updateProcess(index, "step", e.target.value)} />
                </div>
                <div className="col-span-3">
                  <Label>Title</Label>
                  <Input value={step.title} onChange={(e) => updateProcess(index, "title", e.target.value)} />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={step.description} onChange={(e) => updateProcess(index, "description", e.target.value)} rows={2} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Materials Section */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Materials ({content.materials.length})</h3>
          <Button variant="outline" size="sm" onClick={addMaterial}>
            <Plus className="h-4 w-4 mr-2" /> Add Material
          </Button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Section Title</Label>
              <Input 
                value={content.materials_title} 
                onChange={(e) => setContent({ ...content, materials_title: e.target.value })}
              />
            </div>
            <div>
              <Label>Section Subtitle</Label>
              <Input 
                value={content.materials_subtitle} 
                onChange={(e) => setContent({ ...content, materials_subtitle: e.target.value })}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {content.materials.map((mat, index) => (
              <div key={index} className="p-4 border rounded-md space-y-3">
                <div className="flex justify-between items-start">
                  <span className="text-sm font-medium text-muted-foreground">Material {index + 1}</span>
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={() => removeMaterial(index)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div>
                  <Label>Name</Label>
                  <Input value={mat.name} onChange={(e) => updateMaterial(index, "name", e.target.value)} />
                </div>
                <div>
                  <Label>Use</Label>
                  <Input value={mat.use} onChange={(e) => updateMaterial(index, "use", e.target.value)} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* CTA Section */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Call to Action</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>CTA Title</Label>
            <Input 
              value={content.cta_title} 
              onChange={(e) => setContent({ ...content, cta_title: e.target.value })}
            />
          </div>
          <div>
            <Label>CTA Email</Label>
            <Input 
              value={content.cta_email} 
              onChange={(e) => setContent({ ...content, cta_email: e.target.value })}
            />
          </div>
          <div className="col-span-2">
            <Label>CTA Subtitle</Label>
            <Textarea 
              value={content.cta_subtitle} 
              onChange={(e) => setContent({ ...content, cta_subtitle: e.target.value })}
              rows={2}
            />
          </div>
          <div>
            <Label>Button Text</Label>
            <Input 
              value={content.cta_button_text} 
              onChange={(e) => setContent({ ...content, cta_button_text: e.target.value })}
            />
          </div>
        </div>
      </Card>
    </div>
  );
}

// ============= INDUSTRIES TAB =============
function IndustriesTab() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/page/industries`);
      setContent(response.data);
    } catch (error) {
      toast.error("Failed to load industries page content");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/page/industries`, content, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Industries page updated successfully");
    } catch (error) {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const updateIndustry = (index, field, value) => {
    const newIndustries = [...content.industries];
    newIndustries[index] = { ...newIndustries[index], [field]: value };
    setContent({ ...content, industries: newIndustries });
  };

  const updateIndustryArray = (index, field, value) => {
    const newIndustries = [...content.industries];
    newIndustries[index] = { ...newIndustries[index], [field]: value.split(",").map(s => s.trim()).filter(Boolean) };
    setContent({ ...content, industries: newIndustries });
  };

  const addIndustry = () => {
    setContent({
      ...content,
      industries: [...content.industries, { icon: "Package", title: "", description: "", products: [], clients: [], image: "" }]
    });
  };

  const removeIndustry = (index) => {
    const newIndustries = content.industries.filter((_, i) => i !== index);
    setContent({ ...content, industries: newIndustries });
  };

  const updateStat = (index, field, value) => {
    const newStats = [...content.stats];
    newStats[index] = { ...newStats[index], [field]: value };
    setContent({ ...content, stats: newStats });
  };

  const addStat = () => {
    setContent({
      ...content,
      stats: [...content.stats, { value: "", label: "" }]
    });
  };

  const removeStat = (index) => {
    const newStats = content.stats.filter((_, i) => i !== index);
    setContent({ ...content, stats: newStats });
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;
  if (!content) return <div className="text-center py-12">Failed to load content</div>;

  const iconOptions = ["ShoppingCart", "Droplets", "Pill", "Utensils", "Shirt", "PackageOpen", "Car", "Laptop", "Heart", "Home"];

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Industries Page</h2>
        <Button onClick={handleSave} disabled={saving} data-testid="save-industries-btn">
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      {/* Hero Section */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Hero Section</h3>
        <div className="space-y-4">
          <div>
            <Label>Hero Title</Label>
            <Input 
              value={content.hero_title} 
              onChange={(e) => setContent({ ...content, hero_title: e.target.value })}
              data-testid="industries-hero-title"
            />
          </div>
          <div>
            <Label>Hero Subtitle</Label>
            <Textarea 
              value={content.hero_subtitle} 
              onChange={(e) => setContent({ ...content, hero_subtitle: e.target.value })}
              rows={2}
              data-testid="industries-hero-subtitle"
            />
          </div>
        </div>
      </Card>

      {/* Industries Section */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Industries ({content.industries.length})</h3>
          <Button variant="outline" size="sm" onClick={addIndustry}>
            <Plus className="h-4 w-4 mr-2" /> Add Industry
          </Button>
        </div>
        <div className="space-y-6">
          {content.industries.map((ind, index) => (
            <div key={index} className="p-4 border rounded-md space-y-4">
              <div className="flex justify-between items-start">
                <span className="text-sm font-medium text-muted-foreground">Industry {index + 1}</span>
                <Button variant="ghost" size="sm" className="text-destructive" onClick={() => removeIndustry(index)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Icon</Label>
                  <Select value={ind.icon} onValueChange={(v) => updateIndustry(index, "icon", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {iconOptions.map(icon => (
                        <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Title</Label>
                  <Input value={ind.title} onChange={(e) => updateIndustry(index, "title", e.target.value)} />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={ind.description} onChange={(e) => updateIndustry(index, "description", e.target.value)} rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Products (comma-separated)</Label>
                  <Input 
                    value={ind.products.join(", ")} 
                    onChange={(e) => updateIndustryArray(index, "products", e.target.value)} 
                    placeholder="Product 1, Product 2, Product 3"
                  />
                </div>
                <div>
                  <Label>Clients (comma-separated)</Label>
                  <Input 
                    value={ind.clients.join(", ")} 
                    onChange={(e) => updateIndustryArray(index, "clients", e.target.value)} 
                    placeholder="Client 1, Client 2, Client 3"
                  />
                </div>
              </div>
              <div>
                <ImageUpload 
                  value={ind.image} 
                  onChange={(url) => updateIndustry(index, "image", url)}
                  label="Industry Image"
                />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Stats Section */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Stats ({content.stats.length})</h3>
          <Button variant="outline" size="sm" onClick={addStat}>
            <Plus className="h-4 w-4 mr-2" /> Add Stat
          </Button>
        </div>
        <div className="space-y-4">
          <div>
            <Label>Stats Section Title</Label>
            <Input 
              value={content.stats_title} 
              onChange={(e) => setContent({ ...content, stats_title: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {content.stats.map((stat, index) => (
              <div key={index} className="p-4 border rounded-md space-y-3">
                <div className="flex justify-between items-start">
                  <span className="text-sm font-medium text-muted-foreground">Stat {index + 1}</span>
                  <Button variant="ghost" size="sm" className="text-destructive h-6 w-6 p-0" onClick={() => removeStat(index)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
                <div>
                  <Label>Value</Label>
                  <Input value={stat.value} onChange={(e) => updateStat(index, "value", e.target.value)} placeholder="6+" />
                </div>
                <div>
                  <Label>Label</Label>
                  <Input value={stat.label} onChange={(e) => updateStat(index, "label", e.target.value)} placeholder="Major Industries" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* CTA Section */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Call to Action</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>CTA Title</Label>
            <Input 
              value={content.cta_title} 
              onChange={(e) => setContent({ ...content, cta_title: e.target.value })}
            />
          </div>
          <div>
            <Label>CTA Email</Label>
            <Input 
              value={content.cta_email} 
              onChange={(e) => setContent({ ...content, cta_email: e.target.value })}
            />
          </div>
          <div className="col-span-2">
            <Label>CTA Subtitle</Label>
            <Textarea 
              value={content.cta_subtitle} 
              onChange={(e) => setContent({ ...content, cta_subtitle: e.target.value })}
              rows={2}
            />
          </div>
          <div>
            <Label>Button Text</Label>
            <Input 
              value={content.cta_button_text} 
              onChange={(e) => setContent({ ...content, cta_button_text: e.target.value })}
            />
          </div>
        </div>
      </Card>
    </div>
  );
}
// ============= HOME EDITOR TAB =============
function HomeEditorTab() {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [showBlockDialog, setShowBlockDialog] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/page/home`);
      const sortedBlocks = (response.data.blocks || []).sort((a, b) => a.order - b.order);
      setBlocks(sortedBlocks);
    } catch (error) {
      toast.error("Failed to load home page content");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/page/home`, { blocks }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Home page updated successfully");
    } catch (error) {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const moveBlock = (index, direction) => {
    const newBlocks = [...blocks];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newBlocks.length) return;
    
    [newBlocks[index], newBlocks[newIndex]] = [newBlocks[newIndex], newBlocks[index]];
    newBlocks.forEach((block, i) => block.order = i);
    setBlocks(newBlocks);
  };

  const addBlock = (type) => {
    const newBlock = {
      id: `block-${Date.now()}`,
      type,
      title: "",
      subtitle: "",
      content: "",
      buttonText: "",
      buttonLink: "",
      image: "",
      images: [],
      carouselRows: 2,
      carouselCols: 4,
      // Responsive grid settings
      mobileRows: 1,
      mobileCols: 2,
      tabletRows: 2,
      tabletCols: 3,
      desktopRows: 2,
      desktopCols: 4,
      style: {
        fontSize: "text-base",
        fontColor: "#000000",
        backgroundColor: "#ffffff",
        padding: "py-12",
        textAlign: "left"
      },
      order: blocks.length,
      visible: true
    };
    setEditingBlock(newBlock);
    setShowBlockDialog(true);
  };

  const editBlock = (block) => {
    setEditingBlock({ ...block, style: { ...block.style } });
    setShowBlockDialog(true);
  };

  const saveBlock = () => {
    const existingIndex = blocks.findIndex(b => b.id === editingBlock.id);
    if (existingIndex >= 0) {
      const newBlocks = [...blocks];
      newBlocks[existingIndex] = editingBlock;
      setBlocks(newBlocks);
    } else {
      setBlocks([...blocks, editingBlock]);
    }
    setShowBlockDialog(false);
    setEditingBlock(null);
  };

  const deleteBlock = (blockId) => {
    if (!window.confirm("Are you sure you want to delete this block?")) return;
    const newBlocks = blocks.filter(b => b.id !== blockId);
    newBlocks.forEach((block, i) => block.order = i);
    setBlocks(newBlocks);
  };

  const toggleVisibility = (blockId) => {
    const newBlocks = blocks.map(b => 
      b.id === blockId ? { ...b, visible: !b.visible } : b
    );
    setBlocks(newBlocks);
  };

  // Carousel image handlers
  const addCarouselImage = (url) => {
    if (!url) return;
    const newImage = { id: `img-${Date.now()}`, url, alt: "", caption: "" };
    setEditingBlock({
      ...editingBlock,
      images: [...(editingBlock.images || []), newImage]
    });
  };

  const removeCarouselImage = (imageId) => {
    setEditingBlock({
      ...editingBlock,
      images: editingBlock.images.filter(img => img.id !== imageId)
    });
  };

  const updateCarouselImage = (imageId, field, value) => {
    setEditingBlock({
      ...editingBlock,
      images: editingBlock.images.map(img => 
        img.id === imageId ? { ...img, [field]: value } : img
      )
    });
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;

  const blockTypes = [
    { value: "hero", label: "Hero Section", icon: "🎯" },
    { value: "text", label: "Text Block", icon: "📝" },
    { value: "image", label: "Image Block", icon: "🖼️" },
    { value: "carousel", label: "Image Carousel/Grid", icon: "🎠" },
    { value: "cta", label: "Call to Action", icon: "📢" },
    { value: "stats", label: "Stats Section", icon: "📊" },
    { value: "features", label: "Features Grid", icon: "✨" }
  ];

  const fontSizes = [
    { value: "text-sm", label: "Small" },
    { value: "text-base", label: "Normal" },
    { value: "text-lg", label: "Large" },
    { value: "text-xl", label: "Extra Large" },
    { value: "text-2xl", label: "2XL" },
    { value: "text-3xl", label: "3XL" },
    { value: "text-4xl", label: "4XL" },
    { value: "text-5xl", label: "5XL" }
  ];

  const paddings = [
    { value: "py-4", label: "Small" },
    { value: "py-8", label: "Medium" },
    { value: "py-12", label: "Large" },
    { value: "py-16", label: "Extra Large" },
    { value: "py-20", label: "2XL" },
    { value: "py-24", label: "3XL" }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Home Page Editor</h2>
        <div className="flex gap-2">
          <Select onValueChange={(type) => addBlock(type)}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="+ Add Block" />
            </SelectTrigger>
            <SelectContent>
              {blockTypes.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.icon} {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={handleSave} disabled={saving} data-testid="save-home-btn">
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      <p className="text-muted-foreground">
        Drag blocks to reorder, edit styles, and customize your home page content.
      </p>

      {/* Block List */}
      <div className="space-y-4">
        {blocks.map((block, index) => (
          <Card key={block.id} className={`p-4 ${!block.visible ? 'opacity-50' : ''}`}>
            <div className="flex items-center gap-4">
              {/* Order Controls */}
              <div className="flex flex-col gap-1">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => moveBlock(index, 'up')}
                  disabled={index === 0}
                  className="h-6 w-6 p-0"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => moveBlock(index, 'down')}
                  disabled={index === blocks.length - 1}
                  className="h-6 w-6 p-0"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>

              {/* Block Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono bg-primary/10 text-primary px-2 py-0.5 rounded">
                    {block.type.toUpperCase()}
                  </span>
                  <span className="text-xs text-muted-foreground">Order: {index + 1}</span>
                </div>
                <h4 className="font-semibold">{block.title || `Untitled ${block.type} block`}</h4>
                {block.subtitle && <p className="text-sm text-muted-foreground truncate">{block.subtitle}</p>}
                {block.type === 'carousel' && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {block.images?.length || 0} images • {block.carouselRows}x{block.carouselCols} grid
                  </p>
                )}
              </div>

              {/* Preview of style */}
              <div className="hidden md:flex items-center gap-2 text-xs">
                <span className="flex items-center gap-1">
                  <Type className="h-3 w-3" /> {block.style?.fontSize || 'text-base'}
                </span>
                <span 
                  className="w-4 h-4 rounded border" 
                  style={{ backgroundColor: block.style?.fontColor || '#000' }}
                  title={`Font: ${block.style?.fontColor}`}
                />
                <span 
                  className="w-4 h-4 rounded border" 
                  style={{ backgroundColor: block.style?.backgroundColor || '#fff' }}
                  title={`BG: ${block.style?.backgroundColor}`}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-1">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => toggleVisibility(block.id)}
                  title={block.visible ? "Hide" : "Show"}
                >
                  {block.visible ? <CheckCircle className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-gray-400" />}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => editBlock(block)}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" className="text-destructive" onClick={() => deleteBlock(block.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}

        {blocks.length === 0 && (
          <Card className="p-12 text-center">
            <Home className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No blocks yet</h3>
            <p className="text-muted-foreground mb-4">Add blocks to build your home page</p>
          </Card>
        )}
      </div>

      {/* Block Editor Dialog */}
      <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingBlock?.id?.startsWith('block-') ? 'Add' : 'Edit'} Block
            </DialogTitle>
          </DialogHeader>
          
          {editingBlock && (
            <div className="space-y-6">
              {/* Block Type */}
              <div>
                <Label>Block Type</Label>
                <Select 
                  value={editingBlock.type} 
                  onValueChange={(v) => setEditingBlock({ ...editingBlock, type: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {blockTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.icon} {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Content Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Title</Label>
                  <Input 
                    value={editingBlock.title} 
                    onChange={(e) => setEditingBlock({ ...editingBlock, title: e.target.value })}
                    placeholder="Block title"
                  />
                </div>
                <div>
                  <Label>Subtitle</Label>
                  <Input 
                    value={editingBlock.subtitle} 
                    onChange={(e) => setEditingBlock({ ...editingBlock, subtitle: e.target.value })}
                    placeholder="Block subtitle"
                  />
                </div>
              </div>

              {/* Content/Description */}
              {['text', 'hero', 'cta', 'features'].includes(editingBlock.type) && (
                <div>
                  <Label>Content</Label>
                  <Textarea 
                    value={editingBlock.content} 
                    onChange={(e) => setEditingBlock({ ...editingBlock, content: e.target.value })}
                    placeholder="Main content text..."
                    rows={4}
                  />
                </div>
              )}

              {/* Button Fields */}
              {['hero', 'cta'].includes(editingBlock.type) && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Button Text</Label>
                    <Input 
                      value={editingBlock.buttonText} 
                      onChange={(e) => setEditingBlock({ ...editingBlock, buttonText: e.target.value })}
                      placeholder="e.g., Learn More"
                    />
                  </div>
                  <div>
                    <Label>Button Link</Label>
                    <Input 
                      value={editingBlock.buttonLink} 
                      onChange={(e) => setEditingBlock({ ...editingBlock, buttonLink: e.target.value })}
                      placeholder="e.g., /catalog or #access-request"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Use <code className="bg-muted px-1 rounded">#access-request</code> to open the access request form
                    </p>
                  </div>
                </div>
              )}

              {/* Single Image */}
              {['hero', 'image', 'cta'].includes(editingBlock.type) && (
                <div>
                  <ImageUpload 
                    value={editingBlock.image} 
                    onChange={(url) => setEditingBlock({ ...editingBlock, image: url })}
                    label="Background/Main Image"
                  />
                </div>
              )}

              {/* Carousel/Grid Configuration */}
              {editingBlock.type === 'carousel' && (
                <Card className="p-4 space-y-4">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Grid3X3 className="h-4 w-4" /> Image Carousel/Grid Configuration
                  </h4>
                  
                  {/* Enable Slider Toggle */}
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <Label>Enable Slider</Label>
                      <Select 
                        value={editingBlock.enableSlider ? "true" : "false"} 
                        onValueChange={(v) => setEditingBlock({ ...editingBlock, enableSlider: v === "true" })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">Yes - Slide through images</SelectItem>
                          <SelectItem value="false">No - Show grid only</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">Auto-slide & navigation arrows</p>
                    </div>
                  </div>

                  {/* Responsive Grid Settings */}
                  <div className="space-y-4 border rounded-lg p-4 bg-slate-50">
                    <h5 className="font-medium text-sm flex items-center gap-2">
                      <Settings2 className="h-4 w-4" /> Responsive Grid Settings
                    </h5>
                    <p className="text-xs text-muted-foreground">Configure grid layout for different screen sizes</p>

                    {/* Mobile Settings */}
                    <div className="grid grid-cols-3 gap-4 items-end">
                      <div className="col-span-1">
                        <Label className="text-xs font-semibold text-blue-600">📱 Mobile</Label>
                        <p className="text-xs text-muted-foreground">Small screens</p>
                      </div>
                      <div>
                        <Label className="text-xs">Rows</Label>
                        <Input 
                          type="number" 
                          min="1"
                          max="4"
                          value={editingBlock.mobileRows || 1} 
                          onChange={(e) => setEditingBlock({ 
                            ...editingBlock, 
                            mobileRows: Math.max(1, Math.min(4, parseInt(e.target.value) || 1))
                          })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Columns</Label>
                        <Input 
                          type="number"
                          min="1"
                          max="4"
                          value={editingBlock.mobileCols || 2} 
                          onChange={(e) => setEditingBlock({ 
                            ...editingBlock, 
                            mobileCols: Math.max(1, Math.min(4, parseInt(e.target.value) || 2))
                          })}
                        />
                      </div>
                    </div>

                    {/* Tablet Settings */}
                    <div className="grid grid-cols-3 gap-4 items-end">
                      <div className="col-span-1">
                        <Label className="text-xs font-semibold text-green-600">📟 Tablet</Label>
                        <p className="text-xs text-muted-foreground">Medium screens</p>
                      </div>
                      <div>
                        <Label className="text-xs">Rows</Label>
                        <Input 
                          type="number" 
                          min="1"
                          max="6"
                          value={editingBlock.tabletRows || 2} 
                          onChange={(e) => setEditingBlock({ 
                            ...editingBlock, 
                            tabletRows: Math.max(1, Math.min(6, parseInt(e.target.value) || 2))
                          })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Columns</Label>
                        <Input 
                          type="number"
                          min="1"
                          max="6"
                          value={editingBlock.tabletCols || 3} 
                          onChange={(e) => setEditingBlock({ 
                            ...editingBlock, 
                            tabletCols: Math.max(1, Math.min(6, parseInt(e.target.value) || 3))
                          })}
                        />
                      </div>
                    </div>

                    {/* Desktop Settings */}
                    <div className="grid grid-cols-3 gap-4 items-end">
                      <div className="col-span-1">
                        <Label className="text-xs font-semibold text-purple-600">🖥️ Desktop</Label>
                        <p className="text-xs text-muted-foreground">Large screens</p>
                      </div>
                      <div>
                        <Label className="text-xs">Rows</Label>
                        <Input 
                          type="number" 
                          min="1"
                          max="8"
                          value={editingBlock.desktopRows || 2} 
                          onChange={(e) => setEditingBlock({ 
                            ...editingBlock, 
                            desktopRows: Math.max(1, Math.min(8, parseInt(e.target.value) || 2)),
                            carouselRows: Math.max(1, Math.min(8, parseInt(e.target.value) || 2))
                          })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Columns</Label>
                        <Input 
                          type="number"
                          min="1"
                          max="8"
                          value={editingBlock.desktopCols || 4} 
                          onChange={(e) => setEditingBlock({ 
                            ...editingBlock, 
                            desktopCols: Math.max(1, Math.min(8, parseInt(e.target.value) || 4)),
                            carouselCols: Math.max(1, Math.min(8, parseInt(e.target.value) || 4))
                          })}
                        />
                      </div>
                    </div>

                    {/* Preview Summary */}
                    <div className="p-3 bg-white rounded border text-xs">
                      <span className="font-medium">Preview: </span>
                      <span className="text-blue-600">Mobile {editingBlock.mobileRows || 1}×{editingBlock.mobileCols || 2}</span>
                      <span className="mx-2">→</span>
                      <span className="text-green-600">Tablet {editingBlock.tabletRows || 2}×{editingBlock.tabletCols || 3}</span>
                      <span className="mx-2">→</span>
                      <span className="text-purple-600">Desktop {editingBlock.desktopRows || 2}×{editingBlock.desktopCols || 4}</span>
                    </div>
                  </div>

                  {editingBlock.enableSlider && (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-800">
                        <strong>Slider Mode:</strong> Upload unlimited images. The grid adapts per device and slides to reveal more images.
                      </p>
                    </div>
                  )}

                  <div>
                    <Label className="mb-2 block">
                      Images ({editingBlock.images?.length || 0} uploaded)
                      {!editingBlock.enableSlider && (
                        <span className="text-muted-foreground"> / {(editingBlock.desktopRows || 2) * (editingBlock.desktopCols || 4)} max visible on desktop</span>
                      )}
                    </Label>
                    
                    {/* Add Image */}
                    <div className="mb-4">
                      <ImageUpload 
                        value="" 
                        onChange={(url) => addCarouselImage(url)}
                        label="Add Image to Carousel"
                      />
                    </div>

                    {/* Image Grid Preview */}
                    <div 
                      className="grid gap-2 border rounded-lg p-2 bg-gray-50"
                      style={{ gridTemplateColumns: `repeat(${Math.min(editingBlock.desktopCols || 4, 4)}, 1fr)` }}
                    >
                      {(editingBlock.images || []).map((img, idx) => (
                        <div key={img.id} className="relative group">
                          <img 
                            src={img.url} 
                            alt={img.alt || `Image ${idx + 1}`}
                            className="w-full aspect-square object-contain bg-white rounded border"
                          />
                          <button
                            type="button"
                            onClick={() => removeCarouselImage(img.id)}
                            className="absolute top-1 right-1 p-1 bg-destructive text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                          <Input
                            value={img.caption}
                            onChange={(e) => updateCarouselImage(img.id, 'caption', e.target.value)}
                            placeholder="Caption..."
                            className="mt-1 text-xs"
                          />
                        </div>
                      ))}
                      
                      {/* Empty slots - only show when slider is disabled */}
                      {!editingBlock.enableSlider && Array.from({ 
                        length: Math.max(0, ((editingBlock.desktopRows || 2) * (editingBlock.desktopCols || 4)) - (editingBlock.images?.length || 0))
                      }).slice(0, 8).map((_, idx) => (
                        <div 
                          key={`empty-${idx}`} 
                          className="aspect-square border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400"
                        >
                          <ImageIcon className="h-6 w-6" />
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              )}

              {/* Style Controls */}
              <Card className="p-4 space-y-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <Palette className="h-4 w-4" /> Style Settings
                </h4>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Font Size</Label>
                    <Select 
                      value={editingBlock.style?.fontSize || 'text-base'} 
                      onValueChange={(v) => setEditingBlock({ 
                        ...editingBlock, 
                        style: { ...editingBlock.style, fontSize: v } 
                      })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {fontSizes.map(size => (
                          <SelectItem key={size.value} value={size.value}>{size.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Font Color</Label>
                    <div className="flex gap-2 mt-1">
                      <input 
                        type="color" 
                        value={editingBlock.style?.fontColor || '#000000'}
                        onChange={(e) => setEditingBlock({ 
                          ...editingBlock, 
                          style: { ...editingBlock.style, fontColor: e.target.value } 
                        })}
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                      <Input 
                        value={editingBlock.style?.fontColor || '#000000'}
                        onChange={(e) => setEditingBlock({ 
                          ...editingBlock, 
                          style: { ...editingBlock.style, fontColor: e.target.value } 
                        })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Background Color</Label>
                    <div className="flex gap-2 mt-1">
                      <input 
                        type="color" 
                        value={editingBlock.style?.backgroundColor || '#ffffff'}
                        onChange={(e) => setEditingBlock({ 
                          ...editingBlock, 
                          style: { ...editingBlock.style, backgroundColor: e.target.value } 
                        })}
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                      <Input 
                        value={editingBlock.style?.backgroundColor || '#ffffff'}
                        onChange={(e) => setEditingBlock({ 
                          ...editingBlock, 
                          style: { ...editingBlock.style, backgroundColor: e.target.value } 
                        })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Padding</Label>
                    <Select 
                      value={editingBlock.style?.padding || 'py-12'} 
                      onValueChange={(v) => setEditingBlock({ 
                        ...editingBlock, 
                        style: { ...editingBlock.style, padding: v } 
                      })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {paddings.map(p => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Text Alignment</Label>
                  <div className="flex gap-2 mt-2">
                    {['left', 'center', 'right'].map(align => (
                      <Button
                        key={align}
                        type="button"
                        variant={editingBlock.style?.textAlign === align ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setEditingBlock({ 
                          ...editingBlock, 
                          style: { ...editingBlock.style, textAlign: align } 
                        })}
                      >
                        {align.charAt(0).toUpperCase() + align.slice(1)}
                      </Button>
                    ))}
                  </div>
                </div>
              </Card>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowBlockDialog(false)}>Cancel</Button>
                <Button onClick={saveBlock}>Save Block</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Access Form Editor Tab Component
function AccessFormTab() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [requests, setRequests] = useState([]);
  const [showAddField, setShowAddField] = useState(false);
  const [newField, setNewField] = useState({ name: '', label: '', type: 'text', placeholder: '', required: true });

  useEffect(() => {
    fetchConfig();
    fetchRequests();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/access-request/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error("Error fetching config:", error);
      toast.error("Failed to load form configuration");
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/access-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
    } catch (error) {
      console.error("Error fetching requests:", error);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/access-request/config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Form configuration saved successfully");
    } catch (error) {
      console.error("Error saving config:", error);
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (fieldId, key, value) => {
    setConfig(prev => ({
      ...prev,
      fields: prev.fields.map(f => f.id === fieldId ? { ...f, [key]: value } : f)
    }));
  };

  const addField = () => {
    if (!newField.name || !newField.label) {
      toast.error("Please provide field name and label");
      return;
    }
    const fieldId = newField.name.toLowerCase().replace(/\s+/g, '_');
    const field = {
      id: fieldId,
      name: fieldId,
      label: newField.label,
      type: newField.type,
      placeholder: newField.placeholder,
      required: newField.required,
      order: config.fields.length
    };
    setConfig(prev => ({
      ...prev,
      fields: [...prev.fields, field]
    }));
    setNewField({ name: '', label: '', type: 'text', placeholder: '', required: true });
    setShowAddField(false);
    toast.success("Field added. Remember to save changes.");
  };

  const removeField = (fieldId) => {
    // Don't allow removing core fields
    const coreFields = ['full_name', 'company_name', 'email', 'contact_no'];
    if (coreFields.includes(fieldId)) {
      toast.error("Cannot remove core fields");
      return;
    }
    setConfig(prev => ({
      ...prev,
      fields: prev.fields.filter(f => f.id !== fieldId)
    }));
    toast.success("Field removed. Remember to save changes.");
  };

  const moveField = (fieldId, direction) => {
    setConfig(prev => {
      const fields = [...prev.fields];
      const idx = fields.findIndex(f => f.id === fieldId);
      if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === fields.length - 1)) {
        return prev;
      }
      const newIdx = direction === 'up' ? idx - 1 : idx + 1;
      [fields[idx], fields[newIdx]] = [fields[newIdx], fields[idx]];
      // Update order values
      fields.forEach((f, i) => f.order = i);
      return { ...prev, fields };
    });
  };

  const updateRequestStatus = async (requestId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/access-requests/${requestId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(prev => prev.map(r => r.id === requestId ? { ...r, status } : r));
      toast.success("Status updated");
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  if (!config) {
    return <div className="text-center py-12 text-muted-foreground">Failed to load configuration</div>;
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Access Request Form Editor</h2>
          <p className="text-muted-foreground">Configure the catalog access request form shown to visitors</p>
        </div>
        <Button onClick={saveConfig} disabled={saving} data-testid="save-access-form-config">
          {saving ? "Saving..." : "Save Configuration"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Form Configuration */}
        <div className="space-y-6">
          <Card className="p-6 space-y-6">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              Form Settings
            </h3>

            <div className="space-y-4">
              <div>
                <Label>Heading</Label>
                <Input
                  value={config.heading}
                  onChange={(e) => setConfig({ ...config, heading: e.target.value })}
                  placeholder="Form heading"
                  data-testid="access-form-heading"
                />
              </div>

              <div>
                <Label>Subheading</Label>
                <Textarea
                  value={config.subheading}
                  onChange={(e) => setConfig({ ...config, subheading: e.target.value })}
                  placeholder="Form subheading"
                  rows={2}
                  data-testid="access-form-subheading"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Button Text</Label>
                  <Input
                    value={config.buttonText}
                    onChange={(e) => setConfig({ ...config, buttonText: e.target.value })}
                    placeholder="Submit button text"
                    data-testid="access-form-button-text"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Button BG</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={config.buttonBgColor}
                        onChange={(e) => setConfig({ ...config, buttonBgColor: e.target.value })}
                        className="w-12 h-9 p-1"
                        data-testid="access-form-button-bg"
                      />
                      <Input
                        value={config.buttonBgColor}
                        onChange={(e) => setConfig({ ...config, buttonBgColor: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Button Text</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={config.buttonTextColor}
                        onChange={(e) => setConfig({ ...config, buttonTextColor: e.target.value })}
                        className="w-12 h-9 p-1"
                        data-testid="access-form-button-text-color"
                      />
                      <Input
                        value={config.buttonTextColor}
                        onChange={(e) => setConfig({ ...config, buttonTextColor: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6 space-y-6">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              Success Page Settings
            </h3>

            <div className="space-y-4">
              <div>
                <Label>Success Heading</Label>
                <Input
                  value={config.successHeading}
                  onChange={(e) => setConfig({ ...config, successHeading: e.target.value })}
                  placeholder="Success page heading"
                  data-testid="access-form-success-heading"
                />
              </div>

              <div>
                <Label>Success Message</Label>
                <Textarea
                  value={config.successMessage}
                  onChange={(e) => setConfig({ ...config, successMessage: e.target.value })}
                  placeholder="Success page message"
                  rows={3}
                  data-testid="access-form-success-message"
                />
              </div>

              <div>
                <Label>Success Button Text</Label>
                <Input
                  value={config.successButtonText}
                  onChange={(e) => setConfig({ ...config, successButtonText: e.target.value })}
                  placeholder="Back button text"
                  data-testid="access-form-success-button"
                />
              </div>
            </div>
          </Card>

          <Card className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Layers className="h-5 w-5" />
                Form Fields
              </h3>
              <Button size="sm" onClick={() => setShowAddField(true)} data-testid="add-field-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Field
              </Button>
            </div>

            <div className="space-y-3">
              {config.fields.sort((a, b) => a.order - b.order).map((field, idx) => (
                <div key={field.id} className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30">
                  <div className="flex flex-col gap-1">
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveField(field.id, 'up')} disabled={idx === 0}>
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveField(field.id, 'down')} disabled={idx === config.fields.length - 1}>
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{field.label}</div>
                    <div className="text-xs text-muted-foreground">
                      Type: {field.type} | Name: {field.name} {field.required && '| Required'}
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => removeField(field.id)}
                    disabled={['full_name', 'company_name', 'email', 'contact_no'].includes(field.id)}
                    data-testid={`remove-field-${field.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Access Requests List */}
        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="font-semibold text-lg flex items-center gap-2 mb-4">
              <Eye className="h-5 w-5" />
              Access Requests ({requests.length})
            </h3>

            {requests.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No access requests yet
              </div>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {requests.map((req) => (
                  <div key={req.id} className="p-4 border rounded-lg hover:bg-muted/30 transition-colors">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-semibold">{req.full_name}</div>
                        <div className="text-sm text-primary">{req.company_name}</div>
                        <div className="text-sm text-muted-foreground">{req.email}</div>
                        <div className="text-sm text-muted-foreground">{req.contact_no}</div>
                        {Object.entries(req.additional_fields || {}).map(([key, value]) => (
                          <div key={key} className="text-xs text-muted-foreground">
                            {key.replace(/_/g, ' ')}: {value}
                          </div>
                        ))}
                        <div className="text-xs text-muted-foreground mt-2">
                          {new Date(req.created_at).toLocaleString()}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Select 
                          value={req.status} 
                          onValueChange={(v) => updateRequestStatus(req.id, v)}
                        >
                          <SelectTrigger className="w-28 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                          </SelectContent>
                        </Select>
                        <span className={`text-xs px-2 py-1 rounded text-center ${
                          req.status === 'approved' ? 'bg-green-100 text-green-700' :
                          req.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {req.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Add Field Dialog */}
      <Dialog open={showAddField} onOpenChange={setShowAddField}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Form Field</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Field Label</Label>
              <Input
                value={newField.label}
                onChange={(e) => setNewField({ ...newField, label: e.target.value, name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                placeholder="e.g., Company Size"
                data-testid="new-field-label"
              />
            </div>
            <div>
              <Label>Field Type</Label>
              <Select value={newField.type} onValueChange={(v) => setNewField({ ...newField, type: v })}>
                <SelectTrigger data-testid="new-field-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="tel">Phone</SelectItem>
                  <SelectItem value="textarea">Textarea</SelectItem>
                  <SelectItem value="select">Select/Dropdown</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Placeholder</Label>
              <Input
                value={newField.placeholder}
                onChange={(e) => setNewField({ ...newField, placeholder: e.target.value })}
                placeholder="e.g., Enter company size"
                data-testid="new-field-placeholder"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="field-required"
                checked={newField.required}
                onChange={(e) => setNewField({ ...newField, required: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="field-required">Required field</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddField(false)}>Cancel</Button>
            <Button onClick={addField} data-testid="confirm-add-field">Add Field</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Header Editor Tab Component
function HeaderEditorTab() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItem, setNewItem] = useState({ label: '', link: '/', visibleOnDesktop: true, visibleOnTablet: true, visibleOnMobile: true, showInMenu: false });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/config/header`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error("Error fetching header config:", error);
      toast.error("Failed to load header configuration");
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/config/header`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Header configuration saved");
    } catch (error) {
      console.error("Error saving config:", error);
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const addNavItem = () => {
    if (!newItem.label || !newItem.link) {
      toast.error("Please provide label and link");
      return;
    }
    const item = {
      id: Date.now().toString(),
      label: newItem.label,
      link: newItem.link,
      order: config.navItems.length,
      visibleOnDesktop: newItem.visibleOnDesktop,
      visibleOnTablet: newItem.visibleOnTablet,
      visibleOnMobile: newItem.visibleOnMobile,
      showInMenu: newItem.showInMenu
    };
    setConfig({ ...config, navItems: [...config.navItems, item] });
    setNewItem({ label: '', link: '/', visibleOnDesktop: true, visibleOnTablet: true, visibleOnMobile: true, showInMenu: false });
    setShowAddItem(false);
    toast.success("Navigation item added. Remember to save.");
  };

  const removeNavItem = (id) => {
    setConfig({ ...config, navItems: config.navItems.filter(item => item.id !== id) });
    toast.success("Item removed. Remember to save.");
  };

  const updateNavItem = (id, key, value) => {
    setConfig({
      ...config,
      navItems: config.navItems.map(item => item.id === id ? { ...item, [key]: value } : item)
    });
  };

  const moveNavItem = (id, direction) => {
    const items = [...config.navItems];
    const idx = items.findIndex(item => item.id === id);
    if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === items.length - 1)) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
    items.forEach((item, i) => item.order = i);
    setConfig({ ...config, navItems: items });
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  if (!config) {
    return <div className="text-center py-12 text-muted-foreground">Failed to load configuration</div>;
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Header Editor</h2>
          <p className="text-muted-foreground">Configure navigation, logo size, and responsive behavior</p>
        </div>
        <Button onClick={saveConfig} disabled={saving} data-testid="save-header-config">
          {saving ? "Saving..." : "Save Configuration"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Logo Settings */}
        <Card className="p-6 space-y-6">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Logo Settings
          </h3>

          <div className="space-y-4">
            <div>
              <Label>Logo URL</Label>
              <Input
                value={config.logoUrl}
                onChange={(e) => setConfig({ ...config, logoUrl: e.target.value })}
                placeholder="Logo image URL"
                data-testid="header-logo-url"
              />
              {config.logoUrl && (
                <div className="mt-2 p-2 bg-muted rounded">
                  <img src={config.logoUrl} alt="Preview" className="h-12 object-contain" />
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Mobile Height (px)</Label>
                <Input
                  type="number"
                  value={config.logoHeight}
                  onChange={(e) => setConfig({ ...config, logoHeight: parseInt(e.target.value) || 48 })}
                  min={20}
                  max={200}
                  data-testid="header-logo-height-mobile"
                />
              </div>
              <div>
                <Label>Tablet Height (px)</Label>
                <Input
                  type="number"
                  value={config.logoHeightTablet}
                  onChange={(e) => setConfig({ ...config, logoHeightTablet: parseInt(e.target.value) || 56 })}
                  min={20}
                  max={200}
                  data-testid="header-logo-height-tablet"
                />
              </div>
              <div>
                <Label>Desktop Height (px)</Label>
                <Input
                  type="number"
                  value={config.logoHeightDesktop}
                  onChange={(e) => setConfig({ ...config, logoHeightDesktop: parseInt(e.target.value) || 64 })}
                  min={20}
                  max={200}
                  data-testid="header-logo-height-desktop"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Display Settings */}
        <Card className="p-6 space-y-6">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Display Settings
          </h3>

          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Desktop Visible Items</Label>
                <Input
                  type="number"
                  value={config.desktopVisibleCount}
                  onChange={(e) => setConfig({ ...config, desktopVisibleCount: parseInt(e.target.value) || 5 })}
                  min={0}
                  max={10}
                  data-testid="header-desktop-visible"
                />
                <p className="text-xs text-muted-foreground mt-1">Items before "More" menu</p>
              </div>
              <div>
                <Label>Tablet Visible Items</Label>
                <Input
                  type="number"
                  value={config.tabletVisibleCount}
                  onChange={(e) => setConfig({ ...config, tabletVisibleCount: parseInt(e.target.value) || 3 })}
                  min={0}
                  max={10}
                  data-testid="header-tablet-visible"
                />
                <p className="text-xs text-muted-foreground mt-1">Items before menu</p>
              </div>
              <div>
                <Label>Mobile Visible Items</Label>
                <Input
                  type="number"
                  value={config.mobileVisibleCount}
                  onChange={(e) => setConfig({ ...config, mobileVisibleCount: parseInt(e.target.value) || 0 })}
                  min={0}
                  max={10}
                  data-testid="header-mobile-visible"
                />
                <p className="text-xs text-muted-foreground mt-1">0 = all in menu</p>
              </div>
            </div>

            <div className="pt-4 border-t">
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="checkbox"
                  id="show-login"
                  checked={config.showLoginButton}
                  onChange={(e) => setConfig({ ...config, showLoginButton: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="show-login">Show Login Button</Label>
              </div>
              {config.showLoginButton && (
                <div>
                  <Label>Login Button Text</Label>
                  <Input
                    value={config.loginButtonText}
                    onChange={(e) => setConfig({ ...config, loginButtonText: e.target.value })}
                    placeholder="Client Login"
                    data-testid="header-login-text"
                  />
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Navigation Items */}
        <Card className="p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Navigation className="h-5 w-5" />
              Navigation Items ({config.navItems.length})
            </h3>
            <Button size="sm" onClick={() => setShowAddItem(true)} data-testid="add-nav-item-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Item
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2 w-8">Order</th>
                  <th className="text-left py-2 px-2">Label</th>
                  <th className="text-left py-2 px-2">Link</th>
                  <th className="text-center py-2 px-2">Desktop</th>
                  <th className="text-center py-2 px-2">Tablet</th>
                  <th className="text-center py-2 px-2">Mobile</th>
                  <th className="text-center py-2 px-2">In Menu</th>
                  <th className="text-right py-2 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {[...config.navItems].sort((a, b) => a.order - b.order).map((item, idx) => (
                  <tr key={item.id} className="border-b hover:bg-muted/30">
                    <td className="py-2 px-2">
                      <div className="flex flex-col gap-1">
                        <button 
                          onClick={() => moveNavItem(item.id, 'up')} 
                          disabled={idx === 0}
                          className="p-1 hover:bg-muted rounded disabled:opacity-30"
                        >
                          <ChevronUp className="h-3 w-3" />
                        </button>
                        <button 
                          onClick={() => moveNavItem(item.id, 'down')} 
                          disabled={idx === config.navItems.length - 1}
                          className="p-1 hover:bg-muted rounded disabled:opacity-30"
                        >
                          <ChevronDown className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                    <td className="py-2 px-2">
                      <Input
                        value={item.label}
                        onChange={(e) => updateNavItem(item.id, 'label', e.target.value)}
                        className="h-8"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <Input
                        value={item.link}
                        onChange={(e) => updateNavItem(item.id, 'link', e.target.value)}
                        className="h-8"
                      />
                    </td>
                    <td className="py-2 px-2 text-center">
                      <input
                        type="checkbox"
                        checked={item.visibleOnDesktop}
                        onChange={(e) => updateNavItem(item.id, 'visibleOnDesktop', e.target.checked)}
                        className="rounded"
                      />
                    </td>
                    <td className="py-2 px-2 text-center">
                      <input
                        type="checkbox"
                        checked={item.visibleOnTablet}
                        onChange={(e) => updateNavItem(item.id, 'visibleOnTablet', e.target.checked)}
                        className="rounded"
                      />
                    </td>
                    <td className="py-2 px-2 text-center">
                      <input
                        type="checkbox"
                        checked={item.visibleOnMobile}
                        onChange={(e) => updateNavItem(item.id, 'visibleOnMobile', e.target.checked)}
                        className="rounded"
                      />
                    </td>
                    <td className="py-2 px-2 text-center">
                      <input
                        type="checkbox"
                        checked={item.showInMenu}
                        onChange={(e) => updateNavItem(item.id, 'showInMenu', e.target.checked)}
                        className="rounded"
                        title="If checked, this item only shows in hamburger menu"
                      />
                    </td>
                    <td className="py-2 px-2 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => removeNavItem(item.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Add Navigation Item Dialog */}
      <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Navigation Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Label</Label>
              <Input
                value={newItem.label}
                onChange={(e) => setNewItem({ ...newItem, label: e.target.value })}
                placeholder="e.g., Contact Us"
                data-testid="new-nav-label"
              />
            </div>
            <div>
              <Label>Link</Label>
              <Input
                value={newItem.link}
                onChange={(e) => setNewItem({ ...newItem, link: e.target.value })}
                placeholder="e.g., /contact or https://..."
                data-testid="new-nav-link"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="new-desktop"
                  checked={newItem.visibleOnDesktop}
                  onChange={(e) => setNewItem({ ...newItem, visibleOnDesktop: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="new-desktop">Show on Desktop</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="new-tablet"
                  checked={newItem.visibleOnTablet}
                  onChange={(e) => setNewItem({ ...newItem, visibleOnTablet: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="new-tablet">Show on Tablet</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="new-mobile"
                  checked={newItem.visibleOnMobile}
                  onChange={(e) => setNewItem({ ...newItem, visibleOnMobile: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="new-mobile">Show on Mobile</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="new-menu"
                  checked={newItem.showInMenu}
                  onChange={(e) => setNewItem({ ...newItem, showInMenu: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="new-menu">Only in Menu</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItem(false)}>Cancel</Button>
            <Button onClick={addNavItem} data-testid="confirm-add-nav">Add Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Footer Editor Tab Component
function FooterEditorTab() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddLink, setShowAddLink] = useState(false);
  const [showAddSocial, setShowAddSocial] = useState(false);
  const [newLink, setNewLink] = useState({ label: '', url: '/' });
  const [newSocial, setNewSocial] = useState({ platform: 'LinkedIn', url: '' });

  const SOCIAL_PLATFORMS = ['LinkedIn', 'Twitter', 'Facebook', 'Instagram', 'YouTube', 'WhatsApp', 'Other'];

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/config/footer`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error("Error fetching footer config:", error);
      toast.error("Failed to load footer configuration");
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/config/footer`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Footer configuration saved");
    } catch (error) {
      console.error("Error saving config:", error);
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const addQuickLink = () => {
    if (!newLink.label || !newLink.url) {
      toast.error("Please provide label and URL");
      return;
    }
    const link = {
      id: Date.now().toString(),
      label: newLink.label,
      url: newLink.url,
      order: config.quickLinks.length
    };
    setConfig({ ...config, quickLinks: [...config.quickLinks, link] });
    setNewLink({ label: '', url: '/' });
    setShowAddLink(false);
    toast.success("Quick link added. Remember to save.");
  };

  const removeQuickLink = (id) => {
    setConfig({ ...config, quickLinks: config.quickLinks.filter(link => link.id !== id) });
    toast.success("Link removed. Remember to save.");
  };

  const updateQuickLink = (id, key, value) => {
    setConfig({
      ...config,
      quickLinks: config.quickLinks.map(link => link.id === id ? { ...link, [key]: value } : link)
    });
  };

  const moveQuickLink = (id, direction) => {
    const links = [...config.quickLinks];
    const idx = links.findIndex(link => link.id === id);
    if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === links.length - 1)) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    [links[idx], links[newIdx]] = [links[newIdx], links[idx]];
    links.forEach((link, i) => link.order = i);
    setConfig({ ...config, quickLinks: links });
  };

  const addSocialLink = () => {
    if (!newSocial.platform || !newSocial.url) {
      toast.error("Please provide platform and URL");
      return;
    }
    const social = {
      id: Date.now().toString(),
      platform: newSocial.platform,
      url: newSocial.url,
      order: config.socialLinks.length
    };
    setConfig({ ...config, socialLinks: [...config.socialLinks, social] });
    setNewSocial({ platform: 'LinkedIn', url: '' });
    setShowAddSocial(false);
    toast.success("Social link added. Remember to save.");
  };

  const removeSocialLink = (id) => {
    setConfig({ ...config, socialLinks: config.socialLinks.filter(social => social.id !== id) });
    toast.success("Social link removed. Remember to save.");
  };

  const updateSocialLink = (id, key, value) => {
    setConfig({
      ...config,
      socialLinks: config.socialLinks.map(social => social.id === id ? { ...social, [key]: value } : social)
    });
  };

  const moveSocialLink = (id, direction) => {
    const links = [...config.socialLinks];
    const idx = links.findIndex(link => link.id === id);
    if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === links.length - 1)) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    [links[idx], links[newIdx]] = [links[newIdx], links[idx]];
    links.forEach((link, i) => link.order = i);
    setConfig({ ...config, socialLinks: links });
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  if (!config) {
    return <div className="text-center py-12 text-muted-foreground">Failed to load configuration</div>;
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Footer Editor</h2>
          <p className="text-muted-foreground">Configure footer content, links, and social media</p>
        </div>
        <Button onClick={saveConfig} disabled={saving} data-testid="save-footer-config">
          {saving ? "Saving..." : "Save Configuration"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Logo & Company Info */}
        <Card className="p-6 space-y-6">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Logo & Company Info
          </h3>

          <div className="space-y-4">
            <div>
              <Label>Logo URL</Label>
              <Input
                value={config.logoUrl}
                onChange={(e) => setConfig({ ...config, logoUrl: e.target.value })}
                placeholder="Logo image URL"
                data-testid="footer-logo-url"
              />
              {config.logoUrl && (
                <div className="mt-2 p-2 bg-muted rounded">
                  <img src={config.logoUrl} alt="Preview" className="h-12 object-contain" />
                </div>
              )}
            </div>

            <div>
              <Label>Logo Height (px)</Label>
              <Input
                type="number"
                value={config.logoHeight}
                onChange={(e) => setConfig({ ...config, logoHeight: parseInt(e.target.value) || 64 })}
                min={20}
                max={200}
                data-testid="footer-logo-height"
              />
            </div>

            <div>
              <Label>Company Description</Label>
              <Textarea
                value={config.companyDescription}
                onChange={(e) => setConfig({ ...config, companyDescription: e.target.value })}
                placeholder="Brief company description"
                rows={3}
                data-testid="footer-description"
              />
            </div>

            <div>
              <Label>Copyright Text</Label>
              <Input
                value={config.copyrightText}
                onChange={(e) => setConfig({ ...config, copyrightText: e.target.value })}
                placeholder="Company name. All rights reserved."
                data-testid="footer-copyright"
              />
            </div>
          </div>
        </Card>

        {/* Contact Information */}
        <Card className="p-6 space-y-6">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Contact Information
          </h3>

          <div className="space-y-4">
            <div>
              <Label>Email Address</Label>
              <Input
                type="email"
                value={config.contactEmail}
                onChange={(e) => setConfig({ ...config, contactEmail: e.target.value })}
                placeholder="sales@company.com"
                data-testid="footer-email"
              />
            </div>

            <div>
              <Label>Phone Number</Label>
              <Input
                value={config.contactPhone}
                onChange={(e) => setConfig({ ...config, contactPhone: e.target.value })}
                placeholder="+91 1234567890"
                data-testid="footer-phone"
              />
            </div>

            <div>
              <Label>Address</Label>
              <Textarea
                value={config.contactAddress}
                onChange={(e) => setConfig({ ...config, contactAddress: e.target.value })}
                placeholder="Full office address"
                rows={3}
                data-testid="footer-address"
              />
            </div>
          </div>
        </Card>

        {/* Quick Links */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Quick Links ({config.quickLinks.length})
            </h3>
            <Button size="sm" onClick={() => setShowAddLink(true)} data-testid="add-quick-link-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Link
            </Button>
          </div>

          <div className="space-y-2">
            {[...config.quickLinks].sort((a, b) => a.order - b.order).map((link, idx) => (
              <div key={link.id} className="flex items-center gap-2 p-2 border rounded hover:bg-muted/30">
                <div className="flex flex-col">
                  <button 
                    onClick={() => moveQuickLink(link.id, 'up')} 
                    disabled={idx === 0}
                    className="p-0.5 hover:bg-muted rounded disabled:opacity-30"
                  >
                    <ChevronUp className="h-3 w-3" />
                  </button>
                  <button 
                    onClick={() => moveQuickLink(link.id, 'down')} 
                    disabled={idx === config.quickLinks.length - 1}
                    className="p-0.5 hover:bg-muted rounded disabled:opacity-30"
                  >
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </div>
                <Input
                  value={link.label}
                  onChange={(e) => updateQuickLink(link.id, 'label', e.target.value)}
                  placeholder="Label"
                  className="h-8 flex-1"
                />
                <Input
                  value={link.url}
                  onChange={(e) => updateQuickLink(link.id, 'url', e.target.value)}
                  placeholder="URL"
                  className="h-8 flex-1"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-red-500 hover:text-red-700"
                  onClick={() => removeQuickLink(link.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {config.quickLinks.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No quick links added</p>
            )}
          </div>
        </Card>

        {/* Social Media Links */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Social Media Links ({config.socialLinks.length})
            </h3>
            <Button size="sm" onClick={() => setShowAddSocial(true)} data-testid="add-social-link-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Social
            </Button>
          </div>

          <div className="space-y-2">
            {[...config.socialLinks].sort((a, b) => a.order - b.order).map((social, idx) => (
              <div key={social.id} className="flex items-center gap-2 p-2 border rounded hover:bg-muted/30">
                <div className="flex flex-col">
                  <button 
                    onClick={() => moveSocialLink(social.id, 'up')} 
                    disabled={idx === 0}
                    className="p-0.5 hover:bg-muted rounded disabled:opacity-30"
                  >
                    <ChevronUp className="h-3 w-3" />
                  </button>
                  <button 
                    onClick={() => moveSocialLink(social.id, 'down')} 
                    disabled={idx === config.socialLinks.length - 1}
                    className="p-0.5 hover:bg-muted rounded disabled:opacity-30"
                  >
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </div>
                <Select 
                  value={social.platform} 
                  onValueChange={(v) => updateSocialLink(social.id, 'platform', v)}
                >
                  <SelectTrigger className="h-8 w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SOCIAL_PLATFORMS.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={social.url}
                  onChange={(e) => updateSocialLink(social.id, 'url', e.target.value)}
                  placeholder="https://..."
                  className="h-8 flex-1"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-red-500 hover:text-red-700"
                  onClick={() => removeSocialLink(social.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {config.socialLinks.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No social links added</p>
            )}
          </div>
        </Card>
      </div>

      {/* Add Quick Link Dialog */}
      <Dialog open={showAddLink} onOpenChange={setShowAddLink}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Quick Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Label</Label>
              <Input
                value={newLink.label}
                onChange={(e) => setNewLink({ ...newLink, label: e.target.value })}
                placeholder="e.g., Privacy Policy"
                data-testid="new-link-label"
              />
            </div>
            <div>
              <Label>URL</Label>
              <Input
                value={newLink.url}
                onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                placeholder="e.g., /privacy or https://..."
                data-testid="new-link-url"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLink(false)}>Cancel</Button>
            <Button onClick={addQuickLink} data-testid="confirm-add-link">Add Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Social Link Dialog */}
      <Dialog open={showAddSocial} onOpenChange={setShowAddSocial}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Social Media Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Platform</Label>
              <Select value={newSocial.platform} onValueChange={(v) => setNewSocial({ ...newSocial, platform: v })}>
                <SelectTrigger data-testid="new-social-platform">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOCIAL_PLATFORMS.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>URL</Label>
              <Input
                value={newSocial.url}
                onChange={(e) => setNewSocial({ ...newSocial, url: e.target.value })}
                placeholder="https://linkedin.com/company/..."
                data-testid="new-social-url"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSocial(false)}>Cancel</Button>
            <Button onClick={addSocialLink} data-testid="confirm-add-social">Add Social Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Login Page Editor Tab Component
function LoginEditorTab() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/config/login`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error("Error fetching login config:", error);
      toast.error("Failed to load login page configuration");
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/config/login`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Login page configuration saved");
    } catch (error) {
      console.error("Error saving config:", error);
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  if (!config) {
    return <div className="text-center py-12 text-muted-foreground">Failed to load configuration</div>;
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Login Page Editor</h2>
          <p className="text-muted-foreground">Customize the client login page appearance</p>
        </div>
        <Button onClick={saveConfig} disabled={saving} data-testid="save-login-config">
          {saving ? "Saving..." : "Save Configuration"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Logo Settings */}
        <Card className="p-6 space-y-6">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Logo Settings
          </h3>

          <div className="space-y-4">
            <div>
              <Label>Logo URL (optional)</Label>
              <Input
                value={config.logoUrl}
                onChange={(e) => setConfig({ ...config, logoUrl: e.target.value })}
                placeholder="https://... (leave empty for default icon)"
                data-testid="login-logo-url"
              />
              <p className="text-xs text-muted-foreground mt-1">Leave empty to show default building icon</p>
            </div>

            {config.logoUrl && (
              <div className="p-4 bg-muted rounded flex justify-center">
                <img src={config.logoUrl} alt="Logo Preview" style={{ height: `${config.logoHeight}px` }} className="object-contain" />
              </div>
            )}

            <div>
              <Label>Logo Height (px)</Label>
              <Input
                type="number"
                value={config.logoHeight}
                onChange={(e) => setConfig({ ...config, logoHeight: parseInt(e.target.value) || 48 })}
                min={24}
                max={200}
                data-testid="login-logo-height"
              />
            </div>
          </div>
        </Card>

        {/* Text Settings */}
        <Card className="p-6 space-y-6">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <Type className="h-5 w-5" />
            Text Settings
          </h3>

          <div className="space-y-4">
            <div>
              <Label>Heading</Label>
              <Input
                value={config.heading}
                onChange={(e) => setConfig({ ...config, heading: e.target.value })}
                placeholder="Client Portal"
                data-testid="login-heading"
              />
            </div>

            <div>
              <Label>Subheading</Label>
              <Input
                value={config.subheading}
                onChange={(e) => setConfig({ ...config, subheading: e.target.value })}
                placeholder="Enter your credentials to access the catalog"
                data-testid="login-subheading"
              />
            </div>
          </div>
        </Card>

        {/* Info Box Settings */}
        <Card className="p-6 space-y-6 lg:col-span-2">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Info Box Settings (Below Login Button)
          </h3>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="show-info-box"
                checked={config.showInfoBox}
                onChange={(e) => setConfig({ ...config, showInfoBox: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="show-info-box">Show Info Box</Label>
            </div>

            {config.showInfoBox && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Info Box Text</Label>
                  <Textarea
                    value={config.infoBoxText}
                    onChange={(e) => setConfig({ ...config, infoBoxText: e.target.value })}
                    placeholder="Contact your account manager for login credentials"
                    rows={3}
                    data-testid="login-info-text"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Use line breaks to add multiple lines</p>
                </div>

                <div>
                  <Label>Background Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={config.infoBoxBgColor}
                      onChange={(e) => setConfig({ ...config, infoBoxBgColor: e.target.value })}
                      className="w-12 h-9 p-1"
                    />
                    <Input
                      value={config.infoBoxBgColor}
                      onChange={(e) => setConfig({ ...config, infoBoxBgColor: e.target.value })}
                      className="flex-1"
                      data-testid="login-info-bg-color"
                    />
                  </div>
                </div>

                <div>
                  <Label>Text Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={config.infoBoxTextColor}
                      onChange={(e) => setConfig({ ...config, infoBoxTextColor: e.target.value })}
                      className="w-12 h-9 p-1"
                    />
                    <Input
                      value={config.infoBoxTextColor}
                      onChange={(e) => setConfig({ ...config, infoBoxTextColor: e.target.value })}
                      className="flex-1"
                      data-testid="login-info-text-color"
                    />
                  </div>
                </div>

                {/* Preview */}
                <div className="md:col-span-2">
                  <Label>Preview</Label>
                  <div 
                    className="mt-2 p-4 rounded-sm border border-border/50 text-center text-sm"
                    style={{ 
                      backgroundColor: config.infoBoxBgColor,
                      color: config.infoBoxTextColor 
                    }}
                    dangerouslySetInnerHTML={{ __html: config.infoBoxText.replace(/\n/g, '<br/>') }}
                  />
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

// Favicon Editor Tab
function FaviconEditorTab() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/config/favicon`);
      setConfig(response.data);
    } catch (error) {
      console.error("Error fetching favicon config:", error);
      toast.error("Failed to load favicon configuration");
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/config/favicon`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Favicon configuration saved. Refresh the page to see changes.");
    } catch (error) {
      console.error("Error saving config:", error);
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = (field) => async (url) => {
    setConfig(prev => ({ ...prev, [field]: url }));
    toast.success("Image uploaded. Remember to save.");
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  if (!config) {
    return <div className="text-center py-12 text-muted-foreground">Failed to load configuration</div>;
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Favicon Editor</h2>
          <p className="text-muted-foreground">Configure the browser tab icon and app icons for your website</p>
        </div>
        <Button onClick={saveConfig} disabled={saving} data-testid="save-favicon-config">
          {saving ? "Saving..." : "Save Configuration"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Favicon Settings */}
        <Card className="p-6 space-y-6">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Favicon (Browser Tab Icon)
          </h3>
          <p className="text-sm text-muted-foreground">
            This icon appears in browser tabs, bookmarks, and history. Recommended size: 32x32px or 64x64px. Supports ICO, PNG, or SVG formats.
          </p>

          <div className="space-y-4">
            <div>
              <Label>Favicon URL</Label>
              <ImageUpload
                onUpload={handleImageUpload('faviconUrl')}
                currentImage={config.faviconUrl}
                accept="image/*,.ico"
              />
            </div>

            {config.faviconUrl && (
              <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                <div className="flex flex-col items-center gap-2">
                  <span className="text-xs text-muted-foreground">Preview (16px)</span>
                  <img src={config.faviconUrl} alt="Favicon 16" className="w-4 h-4 object-contain" />
                </div>
                <div className="flex flex-col items-center gap-2">
                  <span className="text-xs text-muted-foreground">Preview (32px)</span>
                  <img src={config.faviconUrl} alt="Favicon 32" className="w-8 h-8 object-contain" />
                </div>
                <div className="flex flex-col items-center gap-2">
                  <span className="text-xs text-muted-foreground">Preview (64px)</span>
                  <img src={config.faviconUrl} alt="Favicon 64" className="w-16 h-16 object-contain" />
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Apple Touch Icon */}
        <Card className="p-6 space-y-6">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Apple Touch Icon (iOS/Mobile)
          </h3>
          <p className="text-sm text-muted-foreground">
            This icon is used when users add your website to their home screen on iOS devices. Recommended size: 180x180px PNG.
          </p>

          <div className="space-y-4">
            <div>
              <Label>Apple Touch Icon URL</Label>
              <ImageUpload
                onUpload={handleImageUpload('appleTouchIconUrl')}
                currentImage={config.appleTouchIconUrl}
                accept="image/png"
              />
            </div>

            {config.appleTouchIconUrl && (
              <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                <div className="flex flex-col items-center gap-2">
                  <span className="text-xs text-muted-foreground">Preview (60px)</span>
                  <img src={config.appleTouchIconUrl} alt="Touch Icon" className="w-[60px] h-[60px] object-contain rounded-xl" />
                </div>
                <div className="flex flex-col items-center gap-2">
                  <span className="text-xs text-muted-foreground">Preview (120px)</span>
                  <img src={config.appleTouchIconUrl} alt="Touch Icon" className="w-[120px] h-[120px] object-contain rounded-2xl" />
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Web App Title */}
        <Card className="p-6 space-y-6 lg:col-span-2">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <Type className="h-5 w-5" />
            Web App Title
          </h3>
          <p className="text-sm text-muted-foreground">
            This title is used when the website is added to home screen on mobile devices.
          </p>

          <div className="max-w-md">
            <Label>App Title</Label>
            <Input
              value={config.webAppTitle}
              onChange={(e) => setConfig({ ...config, webAppTitle: e.target.value })}
              placeholder="Your App Name"
              data-testid="favicon-web-app-title"
            />
          </div>
        </Card>
      </div>
    </div>
  );
}